import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import CreatableSelect from "react-select/creatable";
import { Lead, LeadRequest } from "../../feature-module/crm/leads/lead.service";
import { useLeadFormData } from "../../feature-module/crm/leads/leadFormDataContext";
import { Deal, DealRequest } from "../../feature-module/crm/deals/deal.service";
import { useDealFormData } from "../../feature-module/crm/deals/dealFormDataContext";
import { Pipeline, PipelineRequest } from "../../feature-module/crm/pipeline/pipeline.service";
import { usePipelineFormData } from "../../feature-module/crm/pipeline/pipelineFormDataContext";
import { Contact } from "../../feature-module/crm/contacts/contact.service";
import { Company, CompanyService } from "../../feature-module/crm/companies/company.service";
import { useContractFormData } from "../../feature-module/crm/contracts/contractFormDataContext";
import { ContractRequest } from "../../feature-module/crm/contracts/contract.service";
import { useProposalFormData } from "../../feature-module/crm/proposals/proposalFormDataContext";
import { ProposalRequest } from "../../feature-module/crm/proposals/proposal.service";
import { AuthContext } from "../../context/authcontext";
import { getLifecycleTypeName } from "../../utils/lifecycleUtils";
import { API_BASE_URL } from "../../environment";
import { cachedRequest } from "../../utils/requestCache";
import { languageOptions } from "../language";
import { readStoredCurrencyCode } from "../currencyFormat";
import malePng from "../../assets/male.png";
import companyPng from "../../assets/company.png";
import { buildEntityPayload, buildChangedFields } from "./useFormDiff";
import {
  useLifecycleSources,
  useTeamAssignment,
  useContactsCountForCompany,
  fetchContactsForCompany,
  buildValidProductsServices,
  FormLookupsService,
} from "./formLookups.service";
import { FormCtx, SelectOption, UniversalFormConfig } from "./types";

const MySwal = withReactContent(Swal);

const PRIORITY_OPTIONS: SelectOption[] = [
  { value: "Low", label: "Low" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

const isValidPhone = (phone: string): boolean => {
  if (!phone.trim()) return true;
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
  const phoneRegex = /^(\+)?[\d\-\s\(\)]+$/;
  return phoneRegex.test(phone);
};

const filterPhoneInput = (value: string): string => {
  let filtered = value.replace(/[^\d\s\+\(\)\-]/g, "");

  const digits = filtered.replace(/\D/g, "");
  if (digits.length <= 15) return filtered;

  let digitCount = 0;
  let result = "";
  for (const ch of filtered) {
    if (/\d/.test(ch)) {
      if (digitCount >= 15) continue;
      digitCount++;
    }
    result += ch;
  }
  return result;
};

/* ==========================================================================
 * Lead
 * ======================================================================= */

export interface LeadFormProps {
  lead?: Lead;
  onSubmit: (lead: Partial<LeadRequest>) => Promise<void>;
  onCancel: () => void;
  isMainApiCompleted?: boolean;
  isSubmitting?: boolean;
  onRefreshData?: () => void;
  tags?: {
    insert?: string[];
    delete?: number[];
  };
}

interface LeadFormValues {
  title: string;
  company_id: SelectOption | null;
  lifecycle_source_id: number | null;
  score: number | null;
  priority: string;
  preferred_language: string;
  time_zone: string;
  estimated_value: number | null;
  currency_code: string;
  products_n_services: string;
  primary_contact_id: SelectOption | null;
  lifecycle_type_stage_id: number | null;
  assigned_user_id: number | null;
  assigned_team_id: number | null;
}

export function useLeadFormConfig({
  lead,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onRefreshData,
}: LeadFormProps): UniversalFormConfig<LeadFormValues> {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const { lifecycleStages, loading: dataLoading } = useLeadFormData();

  const [existingProductsServices] = useState<any[]>(
    () => (lead?.product_n_service as any[]) || []
  );
  const [createGeneration, setCreateGeneration] = useState(0);

  const apiSourceOptions = useLifecycleSources(selectedTenantId);
  const teamAssignment = useTeamAssignment(
    selectedTenantId,
    lead?.assigned_team?.team_id ?? null
  );
  const contactsCount = useContactsCountForCompany(selectedTenantId);

  const { initialValues, originalSnapshot } = useMemo(() => {
    if (!lead) {
      const values: LeadFormValues = {
        title: "",
        company_id: null,
        lifecycle_source_id: null,
        score: null,
        priority: "Medium",
        preferred_language: "",
        time_zone: "",
        estimated_value: null,
        currency_code: "",
        products_n_services: "",
        primary_contact_id: null,
        lifecycle_type_stage_id: null,
        assigned_user_id: auth?.user_id || null,
        assigned_team_id: null,
      };
      return { initialValues: values, originalSnapshot: null as Record<string, any> | null };
    }

    const companyId = lead.company?.company_id || null;
    const contactId = lead.primary_contact?.contact_id || null;
    const assignedTeamId = lead.assigned_team?.team_id || null;
    const assignedUserId =
      lead.assigned_user_id || lead.assigned_user?.user_id || auth?.user_id || null;
    const lifecycleStageId =
      lead.lifecycle_type_stage?.lifecycle_type_stage_id || null;

    const values: LeadFormValues = {
      title: lead.title || "",
      company_id: companyId
        ? { value: companyId, label: lead.company?.company_name || "" }
        : null,
      lifecycle_source_id: lead.lifecycle_source?.lifecycle_source_id || null,
      score: lead.score || null,
      priority: lead.priority || "",
      preferred_language: lead.preferred_language || "",
      time_zone: lead.time_zone || "",
      estimated_value: lead.estimated_value || null,
      currency_code: lead.currency_code || "",
      products_n_services: lead.products_n_services || "",
      primary_contact_id: contactId
        ? { value: contactId, label: lead.primary_contact?.full_name || "" }
        : null,
      lifecycle_type_stage_id: lifecycleStageId,
      assigned_user_id: assignedUserId,
      assigned_team_id: assignedTeamId,
    };

    const snapshot = {
      title: values.title,
      lifecycle_source_id: values.lifecycle_source_id,
      score: values.score,
      priority: values.priority,
      preferred_language: values.preferred_language,
      time_zone: values.time_zone,
      estimated_value: values.estimated_value,
      currency_code: values.currency_code,
      products_n_services: values.products_n_services,
      company_id: companyId,
      primary_contact_id: contactId,
      lifecycle_type_stage_id: lifecycleStageId,
      assigned_user_id: assignedUserId,
    };

    return { initialValues: values, originalSnapshot: snapshot };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead, auth, createGeneration]);

  const buildSubmitPayload = useCallback(
    (ctx: FormCtx<LeadFormValues>, mode: "create" | "edit") =>
      buildEntityPayload(ctx, mode, {
        fields: [
          "title",
          "lifecycle_source_id",
          "score",
          "priority",
          "preferred_language",
          "time_zone",
          "estimated_value",
          "currency_code",
          "products_n_services",
          "lifecycle_type_stage_id",
          "assigned_user_id",
          "company_id",
          "primary_contact_id",
        ],
        selectFields: ["company_id", "primary_contact_id"],
        rename: { primary_contact_id: "primary_contact" },
        originalSnapshot,
        productsServices: {
          build: buildValidProductsServices,
          tenantId: selectedTenantId,
        },
        extra: (ctx, mode) => {
          if (mode === "create") {
            return {
              tenant_id: selectedTenantId ? parseInt(selectedTenantId) : null,
              assigned_team_id: ctx.values.assigned_team_id,
            };
          }
          const extras: Record<string, any> = {
            assigned_team_id: ctx.values.assigned_team_id,
          };
          const updatedBy = FormLookupsService.getUserIdFromAuthResponseStorage();
          if (updatedBy !== null) extras.updated_by = updatedBy;
          return extras;
        },
      }),
    [originalSnapshot, selectedTenantId]
  );

  const resetCreateUiState = useCallback(() => {
    setCreateGeneration((g) => g + 1);
  }, []);

  const lifecycleStageOptions: SelectOption[] = lifecycleStages.map((stage) => ({
    value: stage.lifecycle_type_stage_id,
    label: stage.stage_name,
  }));

  const sourceOptionsFn = (ctx: FormCtx<LeadFormValues>): SelectOption[] => {
    const currentId = ctx.values.lifecycle_source_id;
    if (
      currentId &&
      !apiSourceOptions.find((o) => o.value === currentId) &&
      lead?.source
    ) {
      return [...apiSourceOptions, { value: currentId, label: lead.source }];
    }
    return apiSourceOptions;
  };

  const formInstanceKey = lead
    ? `lead-${lead.lead_id}`
    : `lead-new-${createGeneration}`;

  return {
    formId: "lead-form",
    mode: lead ? "edit" : "create",
    record: lead ?? null,
    resetKey: formInstanceKey,
    tenantId: selectedTenantId,
    initialValues,
    loading: dataLoading,
    loadingText: "Loading form data...",
    isSubmitting,
    submitLabel: lead
      ? `Update ${getLifecycleTypeName("LEAD")}`
      : `Create ${getLifecycleTypeName("LEAD")}`,
    submittingLabel: lead ? "Updating..." : "Creating...",
    onCancel: () => {
      if (!lead) resetCreateUiState();
      onCancel();
    },
    sections: [
      {
        fields: [
          {
            name: "title",
            label: `${getLifecycleTypeName("LEAD")} Title`,
            type: "text",
            colClass: "col-md-12",
            required: true,
            requiredMessage: "Title is required",
            controlProps: {
              placeholder: `Enter ${getLifecycleTypeName("LEAD")} Title`,
            },
          },
          {
            name: "lifecycle_source_id",
            label: "Source",
            type: "select",
            colClass: "col-md-6",
            required: true,
            requiredMessage: "Source is required",
            isClearable: true,
            options: sourceOptionsFn,
            controlProps: { placeholder: "Select source" },
          },
          {
            name: "priority",
            label: "Priority",
            type: "select",
            colClass: "col-md-6",
            required: true,
            requiredMessage: "Priority is required",
            isClearable: false,
            options: PRIORITY_OPTIONS,
            controlProps: { placeholder: "Select priority" },
          },
          {
            name: "estimated_value",
            label: "Estimated Value",
            type: "number",
            colClass: "col-md-6",
            readOnly: true,
            format: (value) => (value ? value.toFixed(4) : ""),
            controlProps: {
              placeholder: "Auto-calculated from products/services",
              min: "0",
              step: "any",
              disabled: true,
            },
          },
          {
            name: "currency_code",
            label: "Currency",
            type: "currency",
            colClass: "col-md-6",
          },
          {
            name: "products_services_selector",
            type: "products-services",
            colClass: "col-md-12 mb-4",
            label: "Products / Services",
            productsServices: {
              entityType: "lead",
              entityId: lead?.lead_id,
              existingItems: existingProductsServices,
              sumFields: ["discounted_total"],
              writeSumTo: ["estimated_value"],
              skipSumWhenEmptyInEditMode: true,
              onRefreshData,
            },
          },
          {
            name: "preferred_language",
            label: "Preferred Language",
            type: "language",
            colClass: "col-md-6",
          },
          {
            name: "time_zone",
            label: "Time Zone",
            type: "timezone",
            colClass: "col-md-6",
          },
          {
            name: "company_id",
            label: "Company",
            type: "async-select",
            colClass: "col-md-6",
            required: true,
            requiredMessage: "Company is required",
            isClearable: true,
            controlProps: {
              placeholder: "Select company",
              noOptionsMessage: () => "No companies found",
            },
            loadOptions: async (page, search) => {
              const res = await CompanyService.getCompaniesByPage(page, 50, search, true);
              return {
                options: res.companies
                  .filter((c) => c.company_id !== undefined)
                  .map((c) => ({
                    value: c.company_id as number,
                    label: c.company_name,
                  })),
                hasMore: res.hasMore,
              };
            },
            onChange: (value: SelectOption | null, ctx) => {
              ctx.setValue("primary_contact_id", null);
              contactsCount.checkFor(value?.value ?? null);
            },
          },
          {
            name: "primary_contact_id",
            label: "Primary Contact",
            type: "async-select",
            colClass: "col-md-6",
            isClearable: true,
            dependsOn: "company_id",
            disabled: (ctx) => (ctx.values.primary_contact_id ? false : (!ctx.values.company_id || contactsCount.count === 0)),
            placeholder: (ctx) =>
              ctx.values.company_id ? "Select primary contact" : "Select company first",
            helperText: (ctx) =>
              ctx.values.company_id && contactsCount.count === 0 && !contactsCount.isLoading
                ? "No contacts available for this company"
                : "",
            controlProps: {
              noOptionsMessage: () => "No contacts found",
            },
            loadOptions: (page, _search, ctx) =>
              fetchContactsForCompany(
                selectedTenantId,
                ctx.values.company_id?.value ?? null,
                page
              ),
          },
          {
            name: "lifecycle_type_stage_id",
            label: "Lifecycle Stage",
            type: "select",
            colClass: "col-md-6",
            required: true,
            requiredMessage: "Life Stage is required",
            isClearable: true,
            options: lifecycleStageOptions,
            controlProps: { placeholder: "Select life stage" },
          },
          {
            name: "assigned_team_id",
            label: "Assigned Team",
            required: true,
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: teamAssignment.teamOptions,
            controlProps: { placeholder: "Select team" },
            onChange: teamAssignment.onTeamChange,
          },
          {
            name: "assigned_user_id",
            label: "Assigned User",
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: teamAssignment.teamMembers,
            disabled: (ctx) =>
              !ctx.values.assigned_team_id ||
              teamAssignment.isLoadingTeamMembers ||
              teamAssignment.teamMembers.length === 0,
            placeholder: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Please select a team first"
                : teamAssignment.teamMembers.length === 0
                ? "No users in selected team"
                : "Select user from team",
            helperText: (ctx) =>
              !ctx.values.assigned_team_id ? "Select a team first to assign users" : "",
            controlProps: {
              isLoading: teamAssignment.isLoadingTeamMembers,
              noOptionsMessage: () =>
                !teamAssignment.selectedTeamId
                  ? "Please select a team first"
                  : "No users found in selected team",
            },
          },
        ],
      },
    ],
    tags: {
      enabled: true,
      initial: (lead?.tags as any) ?? null,
      debounceMs: 1000,
    },
    customFields: { entityKey: "lifecycle" },
    buildSubmitPayload,
    onSubmit: (payload) => onSubmit(payload),
    onSuccess: (mode) => {
      if (mode === "edit") {
        MySwal.fire({
          icon: "success",
          title: "Updated!",
          text: `${getLifecycleTypeName("LEAD")} has been updated successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });
        if (onRefreshData) {
          setTimeout(() => onRefreshData(), 1000);
        }
      } else {
        resetCreateUiState();
      }
    },
  };
}

/* ==========================================================================
 * Deal
 * ======================================================================= */

export interface DealFormProps {
  deal?: Deal;
  onSubmit: (deal: Partial<DealRequest>) => Promise<void>;
  onCancel: () => void;
  resetTrigger?: number;
  isMainApiCompleted?: boolean;
  isSubmitting?: boolean;
  onRefreshData?: () => void;
  tags?: {
    insert?: string[];
    delete?: number[];
  };
}

interface DealFormValues {
  title: string;
  currency_code: string;
  grand_total: number | null;
  products_n_services: string;
  priority: string;
  lifecycle_source_id: number | null;
  company_id: SelectOption | null;
  primary_contact_id: SelectOption | null;
  _companyContactKey: number | null;
  related_lead_id: number | null;
  related_pipeline_id: number | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  lifecycle_type_stage_id: number | null;
  lifecycle_type_key: string | null;
}

interface DealOriginalSnapshot {
  title: string;
  currency_code: string;
  grand_total: number | null;
  products_n_services: string;
  priority: string;
  lifecycle_source_id: number | null;
  company_id: number | null;
  primary_contact_id: number | null;
  related_lead_id: number | null;
  related_pipeline_id: number | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  lifecycle_type_stage_id: number | null;
  lifecycle_type_key: string | null;
}

export function useDealFormConfig({
  deal,
  onSubmit,
  onCancel,
  resetTrigger,
  isSubmitting = false,
  onRefreshData,
}: DealFormProps): UniversalFormConfig<DealFormValues> {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const {
    contacts,
    lifecycleStages,
    leads,
    pipelines,
    loading: dataLoading,
    error: dataError,
  } = useDealFormData();

  const [existingProductsServices] = useState<any[]>(
    () => (deal?.product_n_service as any[]) || []
  );
  const [hasUnsavedProductChanges, setHasUnsavedProductChanges] = useState(false);

  const [tenantInfo, setTenantInfo] = useState<
    | {
        is_service_tax?: boolean;
        is_gst_tax?: boolean;
        is_vat_tax?: boolean;
        service_tax_default_percentage?: number;
        gst_tax_default_percentage?: number;
        vat_tax_default_percentage?: number;
      }
    | undefined
  >(undefined);

  const [hasGateCompanies, setHasGateCompanies] = useState(false);

  const apiSourceOptions = useLifecycleSources(selectedTenantId);
  const teamAssignment = useTeamAssignment(
    selectedTenantId,
    deal?.assigned_team?.team_id ?? null
  );

  useEffect(() => {
    if (!selectedTenantId) return;
    const fetchTenantInfo = async () => {
      try {
        const data = await cachedRequest(`tenant-info:${selectedTenantId}`, async () => {
          const authHeader = localStorage.getItem("authCred");
          if (!authHeader) return null;
          const response = await fetch(
            `${API_BASE_URL}/tenant/${selectedTenantId}/`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Basic ${authHeader}`,
              },
            }
          );
          if (!response.ok) return null;
          return await response.json();
        });
        if (!data) return;
        setTenantInfo({
          is_service_tax: data.is_service_tax,
          is_gst_tax: data.is_gst_tax,
          is_vat_tax: data.is_vat_tax,
          service_tax_default_percentage: data.service_tax_default_percentage,
          gst_tax_default_percentage: data.gst_tax_default_percentage,
          vat_tax_default_percentage: data.vat_tax_default_percentage,
        });
      } catch (error) {
        console.error("Error fetching tenant info:", error);
      }
    };
    fetchTenantInfo();
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setHasGateCompanies(false);
    CompanyService.getCompaniesByPage(1, 50, "", true)
      .then((res) => {
        if (!cancelled) setHasGateCompanies(res.companies.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasGateCompanies(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  const { initialValues, originalSnapshot } = useMemo(() => {
    if (!deal) {
      const values: DealFormValues = {
        title: "",
        currency_code: "",
        grand_total: null,
        products_n_services: "",
        priority: "Medium",
        lifecycle_source_id: null,
        company_id: null,
        primary_contact_id: null,
        _companyContactKey: null,
        related_lead_id: null,
        related_pipeline_id: null,
        assigned_team_id: null,
        assigned_user_id: auth?.user_id || null,
        lifecycle_type_stage_id: null,
        lifecycle_type_key: null,
      };
      return {
        initialValues: values,
        originalSnapshot: null as DealOriginalSnapshot | null,
      };
    }

    const contactId = deal.primary_contact?.contact_id || null;
    const companyId = deal.company?.company_id || null;
    const assignedUserId = deal.assigned_user?.user_id || null;
    const assignedTeamId = deal.assigned_team?.team_id || null;
    const relatedLeadId = deal.realted_lead?.lead_id || null;
    const relatedPipelineId = deal.related_pipeline?.pipeline_id || null;

    const dealStageId = deal.lifecycle_type_stage?.lifecycle_type_stage_id || null;
    const dealStageKey = deal.lifecycle_type_stage?.lifecycle_type_key || null;
    let lifecycleStageId = dealStageId;
    let lifecycleStageKey = dealStageKey;
    if (dealStageId || dealStageKey) {
      const matchingStage = lifecycleStages.find(
        (stage) =>
          stage.lifecycle_type_stage_id === dealStageId ||
          stage.lifecycle_type_key === dealStageKey
      );
      if (matchingStage) {
        lifecycleStageId = matchingStage.lifecycle_type_stage_id;
        lifecycleStageKey = matchingStage.lifecycle_type_key || "DEAL";
      }
    }

    const contactMatch = contacts.find((c) => c.contact_id === contactId);

    const values: DealFormValues = {
      title: deal.title || "",
      currency_code: deal.currency_code || "",
      grand_total: deal.grand_total || null,
      products_n_services: deal.products_n_services || "",
      priority: deal.priority || "",
      lifecycle_source_id: deal.lifecycle_source?.lifecycle_source_id || null,
      company_id: companyId
        ? { value: companyId, label: deal.company?.company_name || "" }
        : null,
      primary_contact_id:
        contactId && contactMatch
          ? {
              value: contactMatch.contact_id,
              label: `${contactMatch.first_name || ""} ${
                contactMatch.last_name || ""
              }`.trim(),
            }
          : null,
      _companyContactKey: companyId,
      related_lead_id: relatedLeadId,
      related_pipeline_id: relatedPipelineId,
      assigned_team_id: assignedTeamId,
      assigned_user_id: assignedUserId,
      lifecycle_type_stage_id: lifecycleStageId,
      lifecycle_type_key: lifecycleStageKey,
    };

    const snapshot: DealOriginalSnapshot = {
      title: values.title,
      currency_code: values.currency_code,
      grand_total: values.grand_total,
      products_n_services: values.products_n_services,
      priority: values.priority,
      lifecycle_source_id: values.lifecycle_source_id,
      company_id: companyId,
      primary_contact_id: contactId,
      related_lead_id: relatedLeadId,
      related_pipeline_id: relatedPipelineId,
      assigned_team_id: assignedTeamId,
      assigned_user_id: assignedUserId,
      lifecycle_type_stage_id: lifecycleStageId,
      lifecycle_type_key: lifecycleStageKey,
    };

    return { initialValues: values, originalSnapshot: snapshot };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal, contacts, lifecycleStages, auth]);

  const tagsChanged = (ctx: FormCtx<DealFormValues>): boolean => {
    const diff = ctx.getTagsDiff();
    return diff.insert.length > 0 || diff.delete.length > 0;
  };

  const isDirty = useCallback(
    (ctx: FormCtx<DealFormValues>): boolean => {
      if (!originalSnapshot) return true;
      const currentCompanyId = ctx.values.company_id?.value ?? null;
      const currentContactId = ctx.values.primary_contact_id?.value ?? null;
      return (
        ctx.values.title !== originalSnapshot.title ||
        ctx.values.grand_total !== originalSnapshot.grand_total ||
        ctx.values.currency_code !== originalSnapshot.currency_code ||
        ctx.values.priority !== originalSnapshot.priority ||
        ctx.values.lifecycle_source_id !== originalSnapshot.lifecycle_source_id ||
        currentCompanyId !== originalSnapshot.company_id ||
        ctx.values.assigned_user_id !== originalSnapshot.assigned_user_id ||
        ctx.values.lifecycle_type_key !== originalSnapshot.lifecycle_type_key ||
        ctx.values.lifecycle_type_stage_id !==
          originalSnapshot.lifecycle_type_stage_id ||
        ctx.values.related_lead_id !== originalSnapshot.related_lead_id ||
        ctx.values.related_pipeline_id !== originalSnapshot.related_pipeline_id ||
        currentContactId !== originalSnapshot.primary_contact_id ||
        ctx.values.assigned_team_id !== originalSnapshot.assigned_team_id ||
        hasUnsavedProductChanges ||
        tagsChanged(ctx) ||
        ctx.hasCustomFieldChanges
      );
    },
    [originalSnapshot, hasUnsavedProductChanges]
  );

  const buildSubmitPayload = useCallback(
    async (ctx: FormCtx<DealFormValues>, mode: "create" | "edit") => {
      const validProductsServices = buildValidProductsServices(
        ctx.productsServices,
        selectedTenantId
      );
      const customFieldsArray = await ctx.getCustomFieldsPayload();

      if (mode === "create") {
        const payload: any = {
          title: ctx.values.title,
          grand_total: ctx.values.grand_total || undefined,
          currency_code: ctx.values.currency_code,
          priority: ctx.values.priority,
          products_n_services: ctx.values.products_n_services || undefined,
          lifecycle_source_id: ctx.values.lifecycle_source_id || undefined,
          primary_contact: ctx.values.primary_contact_id?.value ?? null,
          company_id: ctx.values.company_id?.value ?? null,
          assigned_user_id: ctx.values.assigned_user_id || undefined,
          lifecycle_type_key: ctx.values.lifecycle_type_key,
          lifecycle_type_stage_id: ctx.values.lifecycle_type_stage_id,
          related_lead_id: ctx.values.related_lead_id ?? undefined,
          related_pipeline_id: ctx.values.related_pipeline_id ?? undefined,
          tenant_id: Number(selectedTenantId),
          created_by: auth!.user_id,
          updated_by: auth!.user_id,
          assigned_team_id: ctx.values.assigned_team_id || undefined,
        };

        if (ctx.tags.length > 0) {
          payload.tags = {
            insert: ctx.tags.map((t) => t.entity_tag_name),
            delete: [],
          };
        }
        if (validProductsServices.length > 0) {
          payload.product_n_service = validProductsServices;
        }
        if (customFieldsArray.length > 0) {
          payload.custom_fields = customFieldsArray;
        }
        return payload;
      }

      const changes: any = {
        updated_by: auth?.user_id || undefined,
        tenant_id: Number(selectedTenantId),
      };

      if (originalSnapshot) {
        Object.assign(
          changes,
          buildChangedFields(ctx.values as any, originalSnapshot as any, [
            "title",
            "currency_code",
            "priority",
          ])
        );

        if (ctx.values.grand_total !== originalSnapshot.grand_total) {
          changes.grand_total = ctx.values.grand_total || undefined;
        }
        if (
          ctx.values.products_n_services !== originalSnapshot.products_n_services
        ) {
          changes.products_n_services =
            ctx.values.products_n_services || undefined;
        }
        if (
          ctx.values.lifecycle_source_id !== originalSnapshot.lifecycle_source_id
        ) {
          changes.lifecycle_source_id =
            ctx.values.lifecycle_source_id || undefined;
        }

        const currentContactId = ctx.values.primary_contact_id?.value ?? null;
        if (currentContactId !== originalSnapshot.primary_contact_id) {
          changes.primary_contact = currentContactId || undefined;
        }
        const currentCompanyId = ctx.values.company_id?.value ?? null;
        if (currentCompanyId !== originalSnapshot.company_id) {
          changes.company_id = currentCompanyId || undefined;
        }
        if (ctx.values.assigned_user_id !== originalSnapshot.assigned_user_id) {
          changes.assigned_user_id = ctx.values.assigned_user_id || undefined;
        }
        if (
          ctx.values.lifecycle_type_key !== originalSnapshot.lifecycle_type_key ||
          ctx.values.lifecycle_type_stage_id !==
            originalSnapshot.lifecycle_type_stage_id
        ) {
          changes.lifecycle_type_key = ctx.values.lifecycle_type_key || undefined;
          changes.lifecycle_type_stage_id =
            ctx.values.lifecycle_type_stage_id || undefined;
        }
        if (ctx.values.related_lead_id !== originalSnapshot.related_lead_id) {
          changes.related_lead_id = ctx.values.related_lead_id ?? null;
        }
        if (
          ctx.values.related_pipeline_id !== originalSnapshot.related_pipeline_id
        ) {
          changes.related_pipeline_id = ctx.values.related_pipeline_id ?? null;
        }
        if (ctx.values.assigned_team_id !== originalSnapshot.assigned_team_id) {
          changes.assigned_team_id = ctx.values.assigned_team_id || undefined;
        }
      }

      const tagsDiff = ctx.getTagsDiff();
      if (tagsDiff.insert.length > 0 || tagsDiff.delete.length > 0) {
        changes.tags = tagsDiff;
      }
      if (validProductsServices.length > 0) {
        changes.product_n_service = validProductsServices;
      }
      if (customFieldsArray.length > 0) {
        changes.custom_fields = customFieldsArray;
      }

      return changes;
    },
    [originalSnapshot, selectedTenantId, auth]
  );

  const isLoadingGate =
    dataLoading &&
    (!hasGateCompanies ||
      !contacts.length ||
      !lifecycleStages.length);

  const lifecycleStageOptions: SelectOption[] = lifecycleStages.map((stage) => ({
    value: stage.lifecycle_type_stage_id,
    label: stage.stage_name,
  }));

  const sourceOptionsFn = (ctx: FormCtx<DealFormValues>): SelectOption[] => {
    const currentId = ctx.values.lifecycle_source_id;
    if (
      currentId &&
      !apiSourceOptions.find((o) => o.value === currentId) &&
      deal?.source
    ) {
      return [...apiSourceOptions, { value: currentId, label: deal.source }];
    }
    return apiSourceOptions;
  };

  const relatedLeadOptionsFn = (
    ctx: FormCtx<DealFormValues>
  ): SelectOption[] => {
    const activeLeads = leads.filter((l) => l.is_active !== false);
    const opts: SelectOption[] = activeLeads
      .filter((l) => l.lead_id != null)
      .map((l) => ({ value: l.lead_id as number, label: l.title }));

    const currentId = ctx.values.related_lead_id;
    if (deal && currentId) {
      const label =
        leads.find((l) => l.lead_id === currentId)?.title ||
        (deal.realted_lead?.lead_id === currentId ? deal.realted_lead?.title : undefined) ||
        `${getLifecycleTypeName("LEAD")} ID: ${currentId}`;
      const idx = opts.findIndex((o) => o.value === currentId);
      if (idx >= 0) {
        opts[idx] = { value: currentId, label };
      } else {
        opts.push({ value: currentId, label });
      }
    }
    return opts;
  };

  const relatedPipelineOptionsFn = (
    ctx: FormCtx<DealFormValues>
  ): SelectOption[] => {
    const activePipelines = (pipelines || []).filter((p) => !p.is_archive);
    const opts: SelectOption[] = activePipelines
      .filter((p) => p.pipeline_id != null)
      .map((p) => ({ value: p.pipeline_id as number, label: p.title }));

    const currentId = ctx.values.related_pipeline_id;
    if (deal && currentId) {
      const label =
        pipelines?.find((p) => p.pipeline_id === currentId)?.title ||
        (deal.related_pipeline?.pipeline_id === currentId ? deal.related_pipeline?.title : undefined) ||
        `${getLifecycleTypeName("PIPELINE")} ID: ${currentId}`;
      const idx = opts.findIndex((o) => o.value === currentId);
      if (idx >= 0) {
        opts[idx] = { value: currentId, label };
      } else {
        opts.push({ value: currentId, label });
      }
    }
    return opts;
  };

  return {
    formId: "deal-form",
    mode: deal ? "edit" : "create",
    record: deal ?? null,
    resetKey: `deal-${deal?.deal_id ?? "new"}-${resetTrigger ?? 0}`,
    tenantId: selectedTenantId,
    initialValues,
    loading: isLoadingGate || !!dataError,
    loadingContent: dataError ? (
      <div>Error loading form data: {dataError}</div>
    ) : (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div
          className="spinner-border spinner-border-sm text-primary me-2"
          role="status"
        >
          <span className="visually-hidden">Loading...</span>
        </div>
        <span className="text-muted">Loading form data...</span>
      </div>
    ),
    isSubmitting,
    submitLabel: deal
      ? `Update ${getLifecycleTypeName("DEAL")}`
      : `Create ${getLifecycleTypeName("DEAL")}`,
    submittingLabel: deal ? "Updating..." : "Creating...",
    onCancel,
    isDirty,
    sections: [
      {
        fields: [
          {
            name: "title",
            label: `${getLifecycleTypeName("DEAL")} Title`,
            type: "text",
            colClass: "col-md-12",
            required: true,
            requiredMessage: `${getLifecycleTypeName("DEAL")} title is required`,
            controlProps: {
              placeholder: `Enter ${getLifecycleTypeName("DEAL")} Title`,
            },
          },
          {
            name: "currency_code",
            label: "Currency",
            type: "currency",
            colClass: "col-md-6",
            controlProps: { isClearable: false },
            onChange: (value: string, ctx) => {
              if (!value) ctx.setValue("currency_code", readStoredCurrencyCode() || "USD");
            },
          },
          {
            name: "grand_total",
            type: "custom",
            render: (ctx) => (
              <div className="col-md-6 mb-3">
                <label className="form-label">Estimated Value</label>
                <input
                  className="form-control"
                  type="number"
                  name="grand_total"
                  value={ctx.values.grand_total?.toFixed(4) || ""}
                  placeholder="Auto-calculated from products/services"
                  min="0"
                  step="any"
                  readOnly
                  disabled
                />
              </div>
            ),
          },
          {
            name: "products_services_selector",
            type: "products-services",
            colClass: "mb-4",
            label: "Products / Services",
            productsServices: {
              entityType: "deal",
              entityId: deal?.deal_id,
              existingItems: existingProductsServices,
              sumFields: [
                "discounted_total",
                "service_tax_total",
                "gst_tax_total",
                "vat_tax_total",
              ],
              writeSumTo: ["grand_total"],
              skipSumWhenEmpty: true,
              onRefreshData,
              onUnsavedChanges: setHasUnsavedProductChanges,
              tenantInfo,
            },
          },
          {
            name: "priority",
            label: "Priority",
            type: "select",
            colClass: "col-md-6",
            required: true,
            options: PRIORITY_OPTIONS,
            controlProps: { placeholder: "Select priority" },
          },
          {
            name: "lifecycle_source_id",
            label: "Source",
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: sourceOptionsFn,
            controlProps: { placeholder: "Select source" },
          },
          {
            name: "company_id",
            label: "Company",
            type: "async-select",
            colClass: "col-md-6",
            required: true,
            isClearable: true,
            controlProps: {
              placeholder: "Select company",
              noOptionsMessage: () => "No companies found",
            },
            loadOptions: async (page, search) => {
              const res = await CompanyService.getCompaniesByPage(
                page,
                50,
                search,
                true
              );
              return {
                options: res.companies
                  .filter((c) => c.company_id !== undefined)
                  .map((c) => ({
                    value: c.company_id as number,
                    label: c.company_name,
                  })),
                hasMore: res.hasMore,
              };
            },
            onChange: (value: SelectOption | null, ctx) => {
              ctx.setValue("_companyContactKey", value?.value ?? null);
              ctx.setValue("primary_contact_id", null);
            },
          },
          {
            name: "primary_contact_id",
            label: "Primary Contact",
            type: "async-select",
            colClass: "col-md-6",
            isClearable: true,
            dependsOn: "_companyContactKey",
            disabled: (ctx) => (ctx.values.primary_contact_id ? false : !ctx.values.company_id),
            placeholder: (ctx) =>
              ctx.values.company_id ? "Select contact" : "Select a company first",
            controlProps: { noOptionsMessage: () => "No contacts found" },
            loadOptions: (page, _search, ctx) =>
              fetchContactsForCompany(
                selectedTenantId,
                ctx.values.company_id?.value ?? null,
                page
              ),
          },
          {
            name: "related_lead_id",
            label: `Related ${getLifecycleTypeName("LEAD")}`,
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: relatedLeadOptionsFn,
            controlProps: {
              placeholder: `Select related ${getLifecycleTypeName("LEAD")}`,
            },
          },
          {
            name: "related_pipeline_id",
            label: `Related ${getLifecycleTypeName("PIPELINE")}`,
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: relatedPipelineOptionsFn,
            controlProps: {
              placeholder: `Select related ${getLifecycleTypeName("PIPELINE")}`,
            },
          },
          {
            name: "assigned_team_id",
            label: "Assigned Team",
            required: true,
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: teamAssignment.teamOptions,
            controlProps: { placeholder: "Select assigned team" },
            onChange: teamAssignment.onTeamChange,
          },
          {
            name: "assigned_user_id",
            label: "Assigned User",
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: teamAssignment.teamMembers,
            disabled: (ctx) =>
              !ctx.values.assigned_team_id ||
              teamAssignment.isLoadingTeamMembers ||
              teamAssignment.teamMembers.length === 0,
            placeholder: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Please select a team first"
                : teamAssignment.teamMembers.length === 0
                ? "No users in selected team"
                : "Select user from team",
            helperText: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Select a team first to assign users"
                : "",
            controlProps: {
              isLoading: teamAssignment.isLoadingTeamMembers,
              noOptionsMessage: () =>
                !teamAssignment.selectedTeamId
                  ? "Please select a team first"
                  : "No users found in selected team",
            },
          },
          {
            name: "lifecycle_type_stage_id",
            label: `${getLifecycleTypeName("DEAL")} Status`,
            type: "select",
            colClass: "col-md-6",
            required: true,
            requiredMessage: `${getLifecycleTypeName("DEAL")} status is required`,
            isClearable: true,
            options: lifecycleStageOptions,
            controlProps: {
              placeholder: `Select ${getLifecycleTypeName("DEAL")} status`,
            },
            onChange: (value: number | null, ctx) => {
              const matchedStage = lifecycleStages.find(
                (s) => s.lifecycle_type_stage_id === value
              );
              ctx.setValue(
                "lifecycle_type_key",
                matchedStage?.lifecycle_type_key || "DEAL"
              );
            },
          },
        ],
      },
    ],
    tags: {
      enabled: true,
      initial: (deal?.tags as any) ?? null,
      debounceMs: 1000,
    },
    customFields: { entityKey: "lifecycle" },
    buildSubmitPayload,
    onSubmit: (payload) => onSubmit(payload),
    onSuccess: (mode) => {
      if (mode === "edit") {
        MySwal.fire({
          icon: "success",
          title: "Updated!",
          text: `${getLifecycleTypeName("DEAL")} has been updated successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });
        if (onRefreshData) {
          setTimeout(() => onRefreshData(), 1000);
        }
      }
    },
  };
}

/* ==========================================================================
 * Pipeline
 * ======================================================================= */

export interface PipelineFormProps {
  pipeline?: Pipeline;
  onSubmit: (data: Partial<PipelineRequest>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  onSuccess?: () => void;
  resetTrigger?: number;
  isMainApiCompleted?: boolean;
  isSubmitting?: boolean;
  onRefreshData?: () => void;
  tags?: {
    insert?: string[];
    delete?: number[];
  };
}

interface PipelineFormValues {
  title: string;
  priority: string;
  products_n_services: string;
  estimated_value: number | null;
  currency_code: string;
  lifecycle_source_id: number | null;
  company_id: SelectOption | null;
  primary_contact_id: SelectOption | null;
  _companyContactKey: number | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  lifecycle_type_stage_id: number | null;
  related_lead_id: number | null;
}

interface PipelineOriginalSnapshot {
  title: string;
  priority: string;
  products_n_services: string;
  estimated_value: number | null;
  currency_code: string;
  lifecycle_source_id: number | null;
  company_id: number | null;
  primary_contact_id: number | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  lifecycle_type_stage_id: number | null;
  related_lead_id: number | null;
}

export function usePipelineFormConfig({
  pipeline,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onRefreshData,
}: PipelineFormProps): UniversalFormConfig<PipelineFormValues> {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const {
    contacts,
    lifecycleStages,
    leads,
    loading: dataLoading,
    error: dataError,
  } = usePipelineFormData();

  const [existingProductsServices] = useState<any[]>(
    () => (pipeline?.product_n_service as any[]) || []
  );
  const [hasGateCompanies, setHasGateCompanies] = useState(false);

  const apiSourceOptions = useLifecycleSources(selectedTenantId);
  const teamAssignment = useTeamAssignment(
    selectedTenantId,
    pipeline?.assigned_team?.team_id ?? null
  );

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setHasGateCompanies(false);
    CompanyService.getCompaniesByPage(1, 50, "", true)
      .then((res) => {
        if (!cancelled) setHasGateCompanies(res.companies.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasGateCompanies(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  const { initialValues, originalSnapshot } = useMemo(() => {
    if (!pipeline) {
      const values: PipelineFormValues = {
        title: "",
        priority: "Medium",
        products_n_services: "",
        estimated_value: null,
        currency_code: "",
        lifecycle_source_id: null,
        company_id: null,
        primary_contact_id: null,
        _companyContactKey: null,
        assigned_team_id: null,
        assigned_user_id: auth?.user_id || null,
        lifecycle_type_stage_id: null,
        related_lead_id: null,
      };
      return { initialValues: values, originalSnapshot: null as PipelineOriginalSnapshot | null };
    }

    const contactId = pipeline.primary_contact?.contact_id || null;
    const companyId = pipeline.company?.company_id || null;
    const assignedUserId = pipeline.assigned_user?.user_id || null;
    const lifecycleStageId =
      pipeline.lifecycle_type_stage?.lifecycle_type_stage_id || null;
    const relatedLeadId = pipeline.realted_lead?.lead_id || null;
    const assignedTeamId = pipeline.assigned_team?.team_id || null;

    const contactMatch = contacts.find((c) => c.contact_id === contactId);

    const values: PipelineFormValues = {
      title: pipeline.title || "",
      priority: pipeline.priority || "",
      products_n_services: pipeline.products_n_services || "",
      estimated_value: pipeline.estimated_value || null,
      currency_code: pipeline.currency_code || "",
      lifecycle_source_id: pipeline.lifecycle_source?.lifecycle_source_id || null,
      company_id: companyId
        ? { value: companyId, label: pipeline.company?.company_name || "" }
        : null,
      primary_contact_id:
        contactId && contactMatch
          ? {
              value: contactMatch.contact_id,
              label: `${contactMatch.first_name || ""} ${
                contactMatch.last_name || ""
              }`.trim(),
            }
          : null,
      _companyContactKey: companyId,
      assigned_team_id: assignedTeamId,
      assigned_user_id: assignedUserId,
      lifecycle_type_stage_id: lifecycleStageId,
      related_lead_id: relatedLeadId,
    };

    const snapshot: PipelineOriginalSnapshot = {
      title: values.title,
      priority: values.priority,
      products_n_services: values.products_n_services,
      estimated_value: values.estimated_value,
      currency_code: values.currency_code,
      lifecycle_source_id: values.lifecycle_source_id,
      company_id: companyId,
      primary_contact_id: contactId,
      assigned_team_id: assignedTeamId,
      assigned_user_id: assignedUserId,
      lifecycle_type_stage_id: lifecycleStageId,
      related_lead_id: relatedLeadId,
    };

    return { initialValues: values, originalSnapshot: snapshot };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline, contacts, auth]);

  const buildSubmitPayload = useCallback(
    (ctx: FormCtx<PipelineFormValues>, mode: "create" | "edit") =>
      buildEntityPayload(ctx, mode, {
        fields: [
          "title",
          "priority",
          "products_n_services",
          "estimated_value",
          "currency_code",
          "lifecycle_source_id",
          "assigned_user_id",
          "lifecycle_type_stage_id",
          "related_lead_id",
          "assigned_team_id",
          "company_id",
          "primary_contact_id",
        ],
        selectFields: ["company_id", "primary_contact_id"],
        rename: { primary_contact_id: "primary_contact" },
        originalSnapshot,
        productsServices: {
          build: buildValidProductsServices,
          tenantId: selectedTenantId,
        },
        extra: (ctx, mode) => {
          if (mode === "create") {
            return {
              tenant_id: selectedTenantId ? parseInt(selectedTenantId) : null,
              assigned_user_id: ctx.values.assigned_user_id || undefined,
            };
          }
          const extras: Record<string, any> = {};
          const updatedBy = FormLookupsService.getUserIdFromAuthResponseStorage();
          if (updatedBy !== null) extras.updated_by = updatedBy;
          return extras;
        },
      }),
    [originalSnapshot, selectedTenantId]
  );

  const lifecycleStageOptions: SelectOption[] = lifecycleStages.map((stage) => ({
    value: stage.lifecycle_type_stage_id,
    label: stage.stage_name,
  }));

  const sourceOptionsFn = (ctx: FormCtx<PipelineFormValues>): SelectOption[] => {
    const currentId = ctx.values.lifecycle_source_id;
    if (
      currentId &&
      !apiSourceOptions.find((o) => o.value === currentId) &&
      pipeline?.source
    ) {
      return [...apiSourceOptions, { value: currentId, label: pipeline.source }];
    }
    return apiSourceOptions;
  };

  const relatedLeadOptionsFn = (
    ctx: FormCtx<PipelineFormValues>
  ): SelectOption[] => {
    const activeLeads = leads.filter((l) => l.is_active !== false);
    const opts: SelectOption[] = activeLeads
      .filter((l) => l.lead_id != null)
      .map((l) => ({ value: l.lead_id as number, label: l.title }));

    const currentId = ctx.values.related_lead_id;
    if (pipeline && currentId) {
      const label =
        leads.find((l) => l.lead_id === currentId)?.title ||
        (pipeline.realted_lead?.lead_id === currentId ? pipeline.realted_lead?.title : undefined) ||
        `${getLifecycleTypeName("LEAD")} ID: ${currentId}`;
      const idx = opts.findIndex((o) => o.value === currentId);
      if (idx >= 0) {
        opts[idx] = { value: currentId, label };
      } else {
        opts.push({ value: currentId, label });
      }
    }
    return opts;
  };

  const showLoadingGate =
    dataLoading &&
    (!hasGateCompanies ||
      !contacts.length ||
      !lifecycleStages.length);

  return {
    formId: "pipeline-form",
    mode: pipeline ? "edit" : "create",
    record: pipeline ?? null,
    resetKey: `pipeline-${pipeline?.pipeline_id ?? "new"}`,
    tenantId: selectedTenantId,
    initialValues,
    loading: showLoadingGate || !!dataError,
    loadingContent: dataError ? (
      <div>Error loading form data: {dataError}</div>
    ) : undefined,
    isSubmitting,
    submitLabel: pipeline
      ? `Update ${getLifecycleTypeName("PIPELINE")}`
      : `Create ${getLifecycleTypeName("PIPELINE")}`,
    submittingLabel: pipeline ? "Updating..." : "Creating...",
    onCancel,
    sections: [
      {
        fields: [
          {
            name: "title",
            label: `${getLifecycleTypeName("PIPELINE")} Title`,
            type: "text",
            colClass: "col-md-12",
            required: true,
            requiredMessage: `${getLifecycleTypeName("PIPELINE")} title is required`,
            controlProps: {
              placeholder: `Enter ${getLifecycleTypeName("PIPELINE")} title`,
            },
          },
          {
            name: "priority",
            label: "Priority",
            type: "select",
            colClass: "col-md-6",
            required: true,
            options: PRIORITY_OPTIONS,
            controlProps: { placeholder: "Select priority" },
          },
          {
            name: "lifecycle_source_id",
            label: "Source",
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: sourceOptionsFn,
            controlProps: { placeholder: "Select source" },
          },
          {
            name: "estimated_value",
            label: "Estimated Value",
            type: "number",
            colClass: "col-md-6",
            readOnly: true,
            format: (value) => (value != null ? value.toFixed(4) : ""),
            controlProps: {
              placeholder: "Auto-calculated from products/services",
              min: "0",
              step: "any",
              disabled: true,
            },
          },
          {
            name: "currency_code",
            label: "Currency",
            type: "currency",
            colClass: "col-md-6",
          },
          {
            name: "products_services_selector",
            type: "products-services",
            colClass: "mb-4",
            label: "Products / Services",
            productsServices: {
              entityType: "pipeline",
              entityId: pipeline?.pipeline_id,
              existingItems: existingProductsServices,
              sumFields: ["discounted_total"],
              writeSumTo: ["estimated_value"],
              emptySumValue: pipeline ? pipeline.estimated_value ?? null : null,
              onRefreshData,
            },
          },
          {
            name: "company_id",
            label: "Company",
            type: "async-select",
            colClass: "col-md-6",
            required: true,
            isClearable: true,
            controlProps: { placeholder: "Select company" },
            loadOptions: async (page, search) => {
              const res = await CompanyService.getCompaniesByPage(page, 50, search, true);
              return {
                options: res.companies
                  .filter((c) => c.company_id !== undefined)
                  .map((c) => ({
                    value: c.company_id as number,
                    label: c.company_name,
                  })),
                hasMore: res.hasMore,
              };
            },
            onChange: (value: SelectOption | null, ctx) => {
              ctx.setValue("_companyContactKey", value?.value ?? null);
              ctx.setValue("primary_contact_id", null);
            },
          },
          {
            name: "primary_contact_id",
            label: "Primary Contact",
            type: "async-select",
            colClass: "col-md-6",
            isClearable: true,
            dependsOn: "_companyContactKey",
            disabled: (ctx) => (ctx.values.primary_contact_id ? false : !ctx.values.company_id),
            placeholder: (ctx) =>
              ctx.values.company_id ? "Select contact" : "Select a company first",
            loadOptions: (page, _search, ctx) =>
              fetchContactsForCompany(
                selectedTenantId,
                ctx.values.company_id?.value ?? null,
                page
              ),
          },
          {
            name: "assigned_team_id",
            label: "Assigned Team",
            required: true,
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: teamAssignment.teamOptions,
            controlProps: { placeholder: "Select assigned team" },
            onChange: teamAssignment.onTeamChange,
          },
          {
            name: "assigned_user_id",
            label: "Assigned User",
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: teamAssignment.teamMembers,
            disabled: (ctx) =>
              !ctx.values.assigned_team_id ||
              teamAssignment.isLoadingTeamMembers ||
              teamAssignment.teamMembers.length === 0,
            placeholder: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Please select a team first"
                : teamAssignment.teamMembers.length === 0
                ? "No users in selected team"
                : "Select user from team",
            helperText: (ctx) =>
              !ctx.values.assigned_team_id ? "Select a team first to assign users" : "",
            controlProps: {
              isLoading: teamAssignment.isLoadingTeamMembers,
              noOptionsMessage: () =>
                !teamAssignment.selectedTeamId
                  ? "Please select a team first"
                  : "No users found in selected team",
            },
          },
          {
            name: "related_lead_id",
            label: `Related ${getLifecycleTypeName("LEAD")}`,
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: relatedLeadOptionsFn,
            controlProps: {
              placeholder: `Select related ${getLifecycleTypeName("LEAD")}`,
            },
          },
          {
            name: "lifecycle_type_stage_id",
            label: "Lifecycle Stage",
            type: "select",
            colClass: "col-md-6",
            required: true,
            requiredMessage: "Lifecycle stage is required",
            isClearable: true,
            options: lifecycleStageOptions,
            controlProps: { placeholder: "Select lifecycle stage" },
          },
        ],
      },
    ],
    tags: {
      enabled: true,
      initial: (pipeline?.tags as any) ?? null,
      debounceMs: 1000,
    },
    customFields: { entityKey: "lifecycle" },
    buildSubmitPayload,
    onSubmit: (payload) => onSubmit(payload),
    onSuccess: (mode) => {
      if (mode === "edit") {
        MySwal.fire({
          icon: "success",
          title: "Updated!",
          text: `${getLifecycleTypeName("PIPELINE")} has been updated successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });
        if (onRefreshData) {
          setTimeout(() => onRefreshData(), 1000);
        }
      }
    },
  };
}

/* ==========================================================================
 * Contact
 * ======================================================================= */

export interface ContactFormProps {
  contact?: Contact;
  onSubmit: (data: Partial<Contact>) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  onSuccess?: () => void;
  resetTrigger?: number;
  preSelectedCompany?: {
    company_id: number;
    company_name: string;
  };
  isMainApiCompleted?: boolean;
  isSubmitting?: boolean;
  tags?: {
    insert?: string[];
    delete?: number[];
  };
}

interface ContactFormValues {
  salutation: string;
  first_name: string;
  last_name: string;
  primary_email: string;
  personal_email: string;
  primary_phone: string;
  mobile_phone: string;
  preferred_language: string;
  job_title: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  source: string;
  lifecycle_source_id: number | null;
  engagement_source: number;
  work_email: string;
  work_phone: string;
  landline: string;
  is_active: boolean;
  company_id: SelectOption | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
}

const salutationOptions: SelectOption[] = [
  { value: "Mr.", label: "Mr." },
  { value: "Mrs.", label: "Mrs." },
  { value: "Ms.", label: "Ms." },
  { value: "Dr.", label: "Dr." },
];

const isValidZipCode = (zipCode: string): boolean => {
  if (!zipCode.trim()) return true;
  const zipCodeRegex = /^\d+$/;
  if (!zipCodeRegex.test(zipCode)) return false;
  return zipCode.length <= 9;
};

const filterZipCodeInput = (value: string): string => {
  let filtered = value.replace(/[^\d]/g, "");
  filtered = filtered.slice(0, 9);
  return filtered;
};

const DEFAULT_AVATAR_FETCH_URL = "/assets/img/profiles/male.png";

export function useContactFormConfig({
  contact,
  onSubmit,
  onCancel,
  resetTrigger,
  preSelectedCompany,
  isSubmitting = false,
}: ContactFormProps): UniversalFormConfig<ContactFormValues> {
  const { auth, selectedTenantId } = useContext(AuthContext);

  const apiSourceOptions = useLifecycleSources(selectedTenantId);
  const teamAssignment = useTeamAssignment(
    selectedTenantId,
    contact?.assigned_team_id ?? null,
    {
      teamLabel: (team: any) =>
        team.name || team.team_name || `Team ${team.team_id}`,
    }
  );

  const initialValues = useMemo<ContactFormValues>(() => {
    if (!contact) {
      return {
        salutation: "Mr.",
        first_name: "",
        last_name: "",
        primary_email: "",
        personal_email: "",
        primary_phone: "",
        mobile_phone: "",
        preferred_language: "",
        job_title: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        country: "",
        zip_code: "",
        source: "",
        lifecycle_source_id: null,
        engagement_source: 1,
        work_email: "",
        work_phone: "",
        landline: "",
        is_active: true,
        company_id: preSelectedCompany
          ? {
              value: preSelectedCompany.company_id,
              label: preSelectedCompany.company_name,
            }
          : null,
        assigned_team_id: null,
        assigned_user_id: null,
      };
    }

    const assignedUserId =
      typeof contact.assigned_user === "object" && contact.assigned_user
        ? contact.assigned_user.user_id
        : contact.assigned_user;

    return {
      salutation: contact.salutation || "Mr.",
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      primary_email: contact.primary_email || "",
      personal_email: contact.personal_email || "",
      primary_phone: contact.primary_phone || "",
      mobile_phone: contact.mobile_phone || "",
      preferred_language: contact.preferred_language || "",
      job_title: contact.job_title || "",
      address_line1: contact.address_line1 || "",
      address_line2: contact.address_line2 || "",
      city: contact.city || "",
      state: contact.state || "",
      country: contact.country || "",
      zip_code: contact.zip_code ? String(contact.zip_code) : "",
      source: contact.source || "",
      lifecycle_source_id:
        contact.lifecycle_source?.lifecycle_source_id ??
        contact.lifecycle_source_id ??
        null,
      engagement_source: contact.engagement_source,
      work_email: contact.work_email || "",
      work_phone: contact.work_phone || "",
      landline: contact.landline || "",
      is_active: contact.is_active,
      company_id: contact.company_id
        ? {
            value: contact.company_id,
            label: contact.company?.company_name || contact.company_name || "",
          }
        : null,
      assigned_team_id: contact.assigned_team_id ?? null,
      assigned_user_id: assignedUserId ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact, preSelectedCompany]);

  const buildSubmitPayload = useCallback(
    async (ctx: FormCtx<ContactFormValues>, _mode: "create" | "edit") => {
      const v = ctx.values;

      const payload: any = {
        salutation: v.salutation,
        first_name: v.first_name,
        last_name: v.last_name,
        primary_email: v.primary_email,
        personal_email: v.personal_email,
        primary_phone: v.primary_phone,
        mobile_phone: v.mobile_phone,
        preferred_language: v.preferred_language,
        job_title: v.job_title,
        address_line1: v.address_line1,
        address_line2: v.address_line2,
        city: v.city,
        state: v.state,
        country: v.country,
        zip_code: v.zip_code,
        source: v.source,
        lifecycle_source_id: v.lifecycle_source_id,
        engagement_source: v.engagement_source,
        work_email: v.work_email,
        work_phone: v.work_phone,
        landline: v.landline,
        is_active: v.is_active,
        company_id: v.company_id?.value ?? null,
        assigned_team_id: v.assigned_team_id,
        assigned_user: v.assigned_user_id,
      };

      const currentUserId = FormLookupsService.getUserIdWithNestedUserFallback(auth);
      if (!contact) {
        if (currentUserId) {
          payload.created_by = currentUserId;
          payload.updated_by = currentUserId;
        }
      } else if (currentUserId) {
        payload.updated_by = currentUserId;
      }
      if (contact && contact.created_by) {
        if (
          typeof contact.created_by === "object" &&
          (contact.created_by as any).user_id
        ) {
          payload.created_by = (contact.created_by as any).user_id;
        } else if (typeof contact.created_by === "number") {
          payload.created_by = contact.created_by;
        }
      }

      payload.tenant_id =
        contact?.tenant_id ||
        (selectedTenantId ? parseInt(selectedTenantId, 10) : undefined);

      if (ctx.imageFile) {
        payload.avatarFile = ctx.imageFile;
      }

      const tagsDiff = ctx.getTagsDiff();
      if (
        tagsDiff.insert.length > 0 ||
        tagsDiff.delete.length > 0 ||
        ctx.tags.length > 0
      ) {
        payload.tags = tagsDiff;
      }

      const customFieldsArray = await ctx.getCustomFieldsPayload();
      if (customFieldsArray.length > 0) {
        payload.custom_fields = ctx.imageFile
          ? JSON.stringify(customFieldsArray)
          : customFieldsArray;
      }

      return payload;
    },
    [contact, auth, selectedTenantId]
  );

  const engagementOptions: SelectOption[] = [
    { value: 1, label: getLifecycleTypeName("LEAD") },
    { value: 2, label: getLifecycleTypeName("DEAL") },
    { value: 3, label: getLifecycleTypeName("PIPELINE") },
    { value: 4, label: getLifecycleTypeName("PROPOSAL") },
    { value: 5, label: getLifecycleTypeName("CONTRACT") },
  ];

  const sourceOptionsFn = (
    ctx: FormCtx<ContactFormValues>
  ): SelectOption[] => {
    const currentId = ctx.values.lifecycle_source_id;
    if (currentId && !apiSourceOptions.find((o) => o.value === currentId)) {
      const fallbackLabel =
        contact?.lifecycle_source?.lifecycle_source_display_name ||
        contact?.source ||
        "Unknown Source";
      return [...apiSourceOptions, { value: currentId, label: fallbackLabel }];
    }
    return apiSourceOptions;
  };

  const resetKey = contact
    ? `contact-${contact.contact_id}`
    : `contact-new-${resetTrigger ?? 0}`;

  return {
    formId: "contact-form",
    mode: contact ? "edit" : "create",
    record: contact ?? null,
    resetKey,
    tenantId: selectedTenantId,
    initialValues,
    isSubmitting,
    submitLabel: contact ? "Update Contact" : "Add Contact",
    submittingLabel: contact ? "Updating..." : "Creating...",
    onCancel,
    sections: [
      {
        title: "Personal Information",
        fields: [
          {
            name: "salutation",
            label: "Salutation",
            type: "select",
            options: salutationOptions,
          },
          {
            name: "first_name",
            label: "First Name",
            type: "text",
          },
          {
            name: "last_name",
            label: "Last Name",
            type: "text",
          },
          {
            name: "preferred_language",
            type: "custom",
            render: (ctx) => (
              <div className="col-md-6 mb-3">
                <label className="form-label">Preferred Language</label>
                <CreatableSelect
                  options={languageOptions}
                  value={
                    languageOptions.find(
                      (o) => o.value === ctx.values.preferred_language
                    ) ||
                    (ctx.values.preferred_language
                      ? {
                          value: ctx.values.preferred_language,
                          label: ctx.values.preferred_language,
                        }
                      : null)
                  }
                  onChange={(selected: any) =>
                    ctx.setValue("preferred_language", selected?.value || "")
                  }
                  onCreateOption={(inputValue: string) =>
                    ctx.setValue("preferred_language", inputValue)
                  }
                  className="select"
                  classNamePrefix="react-select"
                  isClearable
                  placeholder="Select"
                  formatCreateLabel={(inputValue: string) =>
                    `Add "${inputValue}"`
                  }
                />
              </div>
            ),
          },
        ],
      },
      {
        title: "Contact Information",
        fields: [
          {
            name: "primary_email",
            label: "Primary Email",
            type: "email",
            required: true,
          },
          {
            name: "personal_email",
            label: "Personal Email",
            type: "email",
          },
          {
            name: "primary_phone",
            label: "Primary Phone",
            type: "tel",
            required: true,
            onChange: (value: string, ctx) =>
              ctx.setValue("primary_phone", filterPhoneInput(value)),
            validate: (value: string) =>
              value && !isValidPhone(value)
                ? "Please enter a valid phone number with 7 to 15 digits"
                : null,
          },
          {
            name: "mobile_phone",
            label: "Mobile Phone",
            type: "tel",
            controlProps: {
              placeholder: "+66 81 234 5678 or 081 234 5678",
            },
            onChange: (value: string, ctx) =>
              ctx.setValue("mobile_phone", filterPhoneInput(value)),
            validate: (value: string) =>
              value && !isValidPhone(value)
                ? "Please enter a valid phone number with 7 to 15 digits"
                : null,
          },
          {
            name: "landline",
            label: "Landline",
            type: "tel",
            colClass: "col-md-12",
            onChange: (value: string, ctx) =>
              ctx.setValue("landline", filterPhoneInput(value)),
            validate: (value: string) =>
              value && !isValidPhone(value)
                ? "Please enter a valid phone number with 7 to 15 digits"
                : null,
          },
        ],
      },
      {
        title: "Work Information",
        fields: [
          {
            name: "job_title",
            label: "Job Title",
            type: "text",
          },
          {
            name: "company_id",
            label: "Company",
            type: "async-select",
            required: true,
            requiredMessage: "Company is required",
            isClearable: true,
            controlProps: {
              placeholder: "Select a company",
              noOptionsMessage: () => "No companies found",
            },
            loadOptions: async (page, search) => {
              const res = await CompanyService.getCompaniesByPage(
                page,
                50,
                search,
                true
              );
              return {
                options: res.companies
                  .filter((c) => c.company_id !== undefined)
                  .map((c) => ({
                    value: c.company_id as number,
                    label: c.company_name,
                  })),
                hasMore: res.hasMore,
              };
            },
          },
          {
            name: "work_email",
            label: "Work Email",
            type: "email",
          },
          {
            name: "work_phone",
            label: "Work Phone",
            type: "tel",
            onChange: (value: string, ctx) =>
              ctx.setValue("work_phone", filterPhoneInput(value)),
            validate: (value: string) =>
              value && !isValidPhone(value)
                ? "Please enter a valid phone number with 7 to 15 digits"
                : null,
          },
        ],
      },
      {
        title: "Address Information",
        fields: [
          {
            name: "address_line1",
            label: "Address Line 1",
            type: "text",
            colClass: "col-md-12",
          },
          {
            name: "address_line2",
            label: "Address Line 2",
            type: "text",
            colClass: "col-md-12",
          },
          {
            name: "city",
            label: "City",
            type: "text",
          },
          {
            name: "state",
            label: "State",
            type: "text",
          },
          {
            name: "country",
            label: "Country",
            type: "text",
          },
          {
            name: "zip_code",
            label: "ZIP Code",
            type: "text",
            controlProps: { maxLength: 9 },
            onChange: (value: string, ctx) =>
              ctx.setValue("zip_code", filterZipCodeInput(value)),
            validate: (value: string) =>
              value && !isValidZipCode(String(value))
                ? "Please enter valid Zip Code (maximum 9 digits)"
                : null,
          },
        ],
      },
      {
        title: "Additional Information",
        fields: [
          {
            name: "lifecycle_source_id",
            label: "Source",
            type: "select",
            isClearable: true,
            options: sourceOptionsFn,
            controlProps: { placeholder: "Select source" },
          },
          {
            name: "engagement_source",
            label: "Engagement Source",
            type: "select",
            options: engagementOptions,
          },
          {
            name: "assigned_team_id",
            label: "Assign Team",
            required: true,
            type: "select",
            isClearable: true,
            options: teamAssignment.teamOptions,
            controlProps: {
              placeholder: "Select a team",
              noOptionsMessage: () => "No teams found",
            },
            onChange: teamAssignment.onTeamChange,
          },
          {
            name: "assigned_user_id",
            label: "Assign User",
            type: "select",
            isClearable: true,
            options: teamAssignment.teamMembers,
            disabled: (ctx) =>
              !ctx.values.assigned_team_id ||
              teamAssignment.isLoadingTeamMembers ||
              teamAssignment.teamMembers.length === 0,
            placeholder: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Please select a team first"
                : teamAssignment.teamMembers.length === 0
                ? "No users in selected team"
                : "Select user from team",
            helperText: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Select a team first to assign users"
                : "",
            controlProps: {
              isLoading: teamAssignment.isLoadingTeamMembers,
            },
          },
          {
            name: "is_active",
            label: "Active Contact",
            type: "checkbox",
            colClass: "col-md-12",
          },
        ],
      },
    ],
    imageUpload: {
      enabled: true,
      label: "Upload Avatar",
      existingImageUrl: contact?.avatar_url || null,
      placeholderImageUrl: malePng,
      autoDefaultFileUrl: DEFAULT_AVATAR_FETCH_URL,
    },
    tags: {
      enabled: true,
      initial: (contact?.tags as any) ?? null,
      debounceMs: 1000,
    },
    customFields: { entityKey: "contact" },
    buildSubmitPayload,
    onSubmit: (payload) => onSubmit(payload),
  };
}

/* ==========================================================================
 * Company
 * ======================================================================= */

const DEFAULT_LOGO = companyPng;

const SKIP_SUBMIT: unique symbol = Symbol("company-form-no-op");

export interface CompanyFormProps {
  mode: "add" | "edit";
  company?: Company | null;
  onSubmit: (formData: FormData) => void;
  onCancel: () => void;
  loading?: boolean;
  formId: string;
  onSuccess?: () => void;
  resetTrigger?: number;
  isMainApiCompleted?: boolean;
}

interface FormData {
  company_name: string;
  website: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  currency_code: string;
  annual_revenue: string;
  employee_count: string;
  linkedin_url: string;
  facebook_url: string;
  twitter_url: string;
  time_zone: string;
  tax_id: string;
  industry_type_id: number | null;
  business_type_id: number | null;
  assigned_datetime: string | null;
  is_active: boolean;
  created_by:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  updated_by:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  tenant_id: number | null;
  parent_company: number | null;
  assigned_user:
  | number
  | null
  | {
    user_id: number;
    full_name: string;
    email: string;
    phone: string;
    avatar_url: string;
  };
  assigned_team_id: number | null;
  company_logo?: string | null;

  created_datetime?: string;
  updated_datetime?: string;
  created_at?: string;
  updated_at?: string;
  business_type_display_name?: string;
  industry_type_display_name?: string;
  created_by_username?: string;
  updated_by_username?: string;
}

interface CompanyFormValues {
  company_name: string;
  website: string;
  phone: string;
  tax_id: string;
  annual_revenue: string;
  employee_count: number | "";
  business_type_id: number | null;
  industry_type_id: SelectOption | null;
  address: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  currency_code: string;
  time_zone: string;
  assigned_team_id: number | null;
  assigned_user: number | null;
  is_active: boolean;
  linkedin_url: string;
  facebook_url: string;
  twitter_url: string;
  parent_company: number | null;
}

type CompanyOriginalSnapshot = Omit<CompanyFormValues, "industry_type_id"> & {
  industry_type_id: number | null;
};

const BLANK_VALUES: CompanyFormValues = {
  company_name: "",
  website: "",
  phone: "",
  tax_id: "",
  annual_revenue: "",
  employee_count: "",
  business_type_id: null,
  industry_type_id: null,
  address: "",
  city: "",
  state: "",
  country: "",
  zip_code: "",
  currency_code: "",
  time_zone: "",
  assigned_team_id: null,
  assigned_user: null,
  is_active: true,
  linkedin_url: "",
  facebook_url: "",
  twitter_url: "",
  parent_company: null,
};

const EDIT_DIFF_FIELDS: (keyof CompanyFormValues)[] = [
  "company_name",
  "website",
  "phone",
  "tax_id",
  "annual_revenue",
  "employee_count",
  "address",
  "city",
  "state",
  "country",
  "zip_code",
  "currency_code",
  "time_zone",
  "is_active",
  "business_type_id",
  "assigned_team_id",
  "assigned_user",
  "linkedin_url",
  "facebook_url",
  "twitter_url",
];

const CLEARABLE_FIELDS: (keyof CompanyFormValues)[] = [
  "linkedin_url",
  "facebook_url",
  "twitter_url",
  "website",
  "phone",
  "address",
  "city",
  "state",
  "country",
  "zip_code",
  "annual_revenue",
  "employee_count",
  "currency_code",
  "time_zone",
];

const GENERIC_FIELDS: (keyof CompanyFormValues)[] = [
  "company_name",
  "tax_id",
  "business_type_id",
  "is_active",
  "assigned_team_id",
  "assigned_user",
];

export function useCompanyFormConfig({
  mode,
  company,
  onSubmit,
  onCancel,
  loading = false,
  formId,
}: CompanyFormProps): UniversalFormConfig<CompanyFormValues> {
  const { auth, selectedTenantId } = useContext(AuthContext) || {};

  const [businessTypeOptions, setBusinessTypeOptions] = useState<
    SelectOption[]
  >([]);
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false);

  const [resetCounter, setResetCounter] = useState(0);

  const teamAssignment = useTeamAssignment(
    selectedTenantId ?? null,
    company?.assigned_team?.team_id ?? null
  );

  useEffect(() => {
    let cancelled = false;
    const fetchBusinessTypes = async () => {
      setIsLoadingDropdowns(true);
      try {
        const all: any[] = await cachedRequest("business-types:all", async () => {
          const authHeader = localStorage.getItem("authCred");
          if (!authHeader) return [];

          let collected: any[] = [];
          let page = 1;
          let hasMore = true;

          while (hasMore) {
            const response = await fetch(
              `${API_BASE_URL}/business-type/?page=${page}&page_size=1000&response_mode=light`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Basic ${authHeader}`,
                },
              }
            );
            if (!response.ok) {
              throw new Error(
                `Failed to fetch business types: ${response.status}`
              );
            }
            const data = await response.json();

            let items: any[] = [];
            if (Array.isArray(data)) {
              items = data;
              hasMore = false;
            } else if (Array.isArray(data.results)) {
              items = data.results;
              hasMore = data.next !== null;
            } else if (Array.isArray(data.data)) {
              items = data.data;
              hasMore = data.next !== null;
            } else {
              hasMore = false;
            }

            collected = [...collected, ...items];
            page++;
            if (page > 5) hasMore = false;
          }

          return collected;
        });

        if (!cancelled) {
          setBusinessTypeOptions(
            all.map((item) => ({
              value: item.business_type_id,
              label: item.business_type_display_name,
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching business types:", err);
      } finally {
        if (!cancelled) setIsLoadingDropdowns(false);
      }
    };
    fetchBusinessTypes();
    return () => {
      cancelled = true;
    };
  }, []);

  const { initialValues, originalSnapshot } = useMemo(() => {
    if (mode !== "edit" || !company) {
      return {
        initialValues: { ...BLANK_VALUES },
        originalSnapshot: null as CompanyOriginalSnapshot | null,
      };
    }

    const assignedUserId =
      typeof company.assigned_user === "object" && company.assigned_user
        ? company.assigned_user.user_id
        : (company.assigned_user as number | null) || null;
    const assignedTeamId =
      (company.assigned_team && company.assigned_team.team_id) || null;

    const values: CompanyFormValues = {
      company_name: company.company_name || "",
      website: company.website || "",
      phone: company.phone || "",
      tax_id: company.tax_id || "",
      annual_revenue: company.annual_revenue || "",
      employee_count: (company.employee_count as any) || "",
      business_type_id: company.business_type_id || null,
      industry_type_id: company.industry_type_id
        ? {
          value: company.industry_type_id,
          label:
            company.industry_type_display_name ||
            String(company.industry_type_id),
        }
        : null,
      address: company.address || "",
      city: company.city || "",
      state: company.state || "",
      country: company.country || "",
      zip_code: company.zip_code || "",
      currency_code: company.currency_code || "",
      time_zone: company.time_zone || "",
      assigned_team_id: assignedTeamId,
      assigned_user: assignedUserId,
      is_active: company.is_active !== undefined ? company.is_active : true,
      linkedin_url: company.linkedin_url || "",
      facebook_url: company.facebook_url || "",
      twitter_url: company.twitter_url || "",
      parent_company: null,
    };

    const snapshot: CompanyOriginalSnapshot = {
      ...values,
      industry_type_id: company.industry_type_id || null,
    };

    return { initialValues: values, originalSnapshot: snapshot };
  }, [mode, company]);

  const buildSubmitPayload = async (
    ctx: FormCtx<CompanyFormValues>,
    engineMode: "create" | "edit"
  ): Promise<any> => {
    const userId = FormLookupsService.getUserIdWithProfileFallback(auth);

    if (engineMode === "create") {
      const payload: any = {};

      CLEARABLE_FIELDS.forEach((key) => {
        payload[key] = (ctx.values as any)[key] || "";
      });

      GENERIC_FIELDS.forEach((key) => {
        const v = (ctx.values as any)[key];
        if (v !== null && v !== undefined && v !== "") payload[key] = v;
      });

      const industryTypeIdValue = ctx.values.industry_type_id?.value ?? null;
      if (
        industryTypeIdValue !== null &&
        industryTypeIdValue !== undefined &&
        industryTypeIdValue !== ""
      ) {
        payload.industry_type_id = industryTypeIdValue;
      }

      const parentCompany = ctx.values.parent_company as any;
      if (
        parentCompany !== null &&
        parentCompany !== undefined &&
        parentCompany !== "" &&
        !(
          typeof parentCompany === "object" &&
          Object.keys(parentCompany).length === 0
        )
      ) {
        payload.parent_company = parentCompany;
      }

      if (userId) {
        payload.created_by = userId;
        payload.updated_by = userId;
      }

      if (ctx.imageFile) {
        payload.logoFile = ctx.imageFile;
      }

      const customFieldsArray = await ctx.getCustomFieldsPayload();
      if (customFieldsArray.length > 0) {
        payload.custom_fields = JSON.stringify(customFieldsArray);
      }

      const tagsDiff = ctx.getTagsDiff();
      if (tagsDiff.insert.length > 0 || tagsDiff.delete.length > 0) {
        payload.tags = tagsDiff;
      }

      return payload;
    }

    const changes: any = originalSnapshot
      ? buildChangedFields(
        ctx.values as any,
        originalSnapshot as any,
        EDIT_DIFF_FIELDS as string[]
      )
      : {};

    if (originalSnapshot) {
      const currentIndustryTypeId = ctx.values.industry_type_id?.value ?? null;
      if (currentIndustryTypeId !== originalSnapshot.industry_type_id) {
        changes.industry_type_id = currentIndustryTypeId;
      }
    }

    if (ctx.imageFile) {
      changes.logoFile = ctx.imageFile;
    }

    const customFieldsArray = await ctx.getCustomFieldsPayload();
    if (customFieldsArray.length > 0) {
      changes.custom_fields = ctx.imageFile
        ? JSON.stringify(customFieldsArray)
        : customFieldsArray;
    }

    const tagsDiff = ctx.getTagsDiff();
    if (tagsDiff.insert.length > 0 || tagsDiff.delete.length > 0) {
      changes.tags = tagsDiff;
    }

    if (userId) {
      changes.updated_by = userId;
    }

    const changeKeys = Object.keys(changes);
    if (
      changeKeys.length === 1 &&
      !!changes.updated_by &&
      !changes.custom_fields
    ) {
      return SKIP_SUBMIT;
    }

    return changes;
  };

  const resetKey = `company-${mode}-${company?.company_id ?? "new"}-${resetCounter}`;

  return {
    formId,
    mode: mode === "edit" ? "edit" : "create",
    record: company ?? null,
    resetKey,
    tenantId: selectedTenantId ?? null,
    initialValues,
    loading: false,
    isSubmitting: loading,
    submitLabel: mode === "add" ? "Add Company" : "Update Company",
    submittingLabel: mode === "add" ? "Creating..." : "Updating...",
    onCancel: () => {
      if (mode === "add") {
        setResetCounter((c) => c + 1);
      }
      onCancel();
    },
    sections: [
      {
        title: "Company Information",
        fields: [
          {
            name: "company_name",
            label: "Company Name",
            type: "text",
            colClass: "col-12",
            required: true,
            requiredMessage: "Company name is required",
            placeholder: "Enter company name",
          },
          {
            name: "website",
            label: "Website",
            type: "url",
            colClass: "col-md-6",
          },
          {
            name: "phone",
            label: "Phone",
            type: "tel",
            colClass: "col-md-6",
            placeholder: "+1 555 123 4567 or 081 234 5678",
            validate: (value: string) => {
              if (!value || !String(value).trim()) return null;
              return isValidPhone(value)
                ? null
                : "Please enter a valid phone number with at least 7 digits";
            },
            onChange: (value: string, ctx) => {
              ctx.setValue("phone", filterPhoneInput(value ?? ""));
            },
          },
          {
            name: "tax_id",
            label: "Tax ID",
            type: "text",
            colClass: "col-12",
            placeholder: "Enter tax identification number",
          },
          {
            name: "annual_revenue",
            label: "Annual Revenue",
            type: "number",
            colClass: "col-md-6",
            controlProps: { step: "0.01", min: "0" },
          },
          {
            name: "employee_count",
            label: "Employee Count",
            type: "number",
            colClass: "col-md-6",
            controlProps: { step: "1", min: "0" },
            onChange: (value: any, ctx) => {
              let coerced: any = value ? Math.floor(Number(value)) : "";
              if (isNaN(coerced)) coerced = "";
              ctx.setValue("employee_count", coerced);
            },
          },
          {
            name: "business_type_id",
            label: "Business Type",
            type: "select",
            colClass: "col-md-6",
            isClearable: true,
            options: businessTypeOptions,
            disabled: () => isLoadingDropdowns,
            placeholder: "Select business type",
            controlProps: { isLoading: isLoadingDropdowns },
          },
          {
            name: "industry_type_id",
            label: "Industry Type",
            type: "async-select",
            colClass: "col-md-6",
            isClearable: true,
            placeholder: "Select industry type",
            loadOptions: async (page: number) => {
              const authHeader = localStorage.getItem("authCred");
              if (!authHeader) return { options: [], hasMore: false };
              const response = await fetch(
                `${API_BASE_URL}/industry-type/?page=${page}&page_size=200&response_mode=light`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${authHeader}`,
                  },
                }
              );
              if (!response.ok) return { options: [], hasMore: false };
              const data = await response.json();
              let items: any[] = [];
              if (Array.isArray(data)) items = data;
              else if (Array.isArray(data.results)) items = data.results;
              else if (Array.isArray(data.data)) items = data.data;
              return {
                options: items.map((item: any) => ({
                  value: item.industry_type_id,
                  label: item.industry_type_display_name,
                })),
                hasMore: page < 2,
              };
            },
          },
        ],
      },
      {
        title: "Address Information",
        fields: [
          {
            name: "address",
            label: "Address",
            type: "text",
            colClass: "col-12",
          },
          { name: "city", label: "City", type: "text", colClass: "col-md-6" },
          {
            name: "state",
            label: "State",
            type: "text",
            colClass: "col-md-6",
          },
          {
            name: "country",
            label: "Country",
            type: "text",
            colClass: "col-md-6",
          },
          {
            name: "zip_code",
            label: "ZIP Code",
            type: "number",
            colClass: "col-md-6",
            controlProps: { step: "1", min: "0" },
            onChange: (value: any, ctx) => {
              ctx.setValue("zip_code", String(value ?? "").replace(/\D/g, ""));
            },
          },
        ],
      },
      {
        title: "Business Settings",
        fields: [
          // {
          //   name: "currency_code",
          //   label: "Currency",
          //   type: "currency",
          //   colClass: "col-md-6",
          // },
          {
            name: "time_zone",
            label: "Time Zone",
            type: "timezone",
            colClass: "col-md-6",
          },
          {
            name: "assigned_team_id",
            label: "Assign Team",
            required: true,
            type: "select",
            colClass: "col-12",
            isClearable: true,
            options: teamAssignment.teamOptions,
            placeholder: "Select team to assign",
            onChange: (value: number | null, ctx) => {
              ctx.setValue("assigned_user", null);
              teamAssignment.onTeamChange(value, ctx);
            },
          },
          {
            name: "assigned_user",
            label: "Assign User",
            type: "select",
            colClass: "col-12",
            isClearable: true,
            options: teamAssignment.teamMembers,
            disabled: (ctx) =>
              !ctx.values.assigned_team_id ||
              teamAssignment.isLoadingTeamMembers ||
              teamAssignment.teamMembers.length === 0,
            placeholder: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Please select a team first"
                : teamAssignment.teamMembers.length === 0
                  ? "No users in selected team"
                  : "Select user from team",
            helperText: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Select a team first to assign users"
                : "",
            controlProps: { isLoading: teamAssignment.isLoadingTeamMembers },
          },
          {
            name: "is_active",
            label: "Active Company",
            type: "checkbox",
            colClass: "col-12",
          },
        ],
      },
      {
        title: "Social Media",
        fields: [
          {
            name: "linkedin_url",
            label: "LinkedIn URL",
            type: "url",
            colClass: "col-12",
          },
          {
            name: "facebook_url",
            label: "Facebook URL",
            type: "url",
            colClass: "col-md-6",
          },
          {
            name: "twitter_url",
            label: "Twitter URL",
            type: "url",
            colClass: "col-md-6",
          },
        ],
      },
    ],
    imageUpload: {
      enabled: true,
      label: "Upload Logo",
      existingImageUrl: company?.company_logo || null,
      placeholderImageUrl: DEFAULT_LOGO,
    },
    tags: {
      enabled: true,
      initial: (company?.tags as any) ?? null,
      debounceMs: 1000,
    },
    customFields: { entityKey: "company" },
    buildSubmitPayload,
    onSubmit: async (payload: any) => {
      if (payload === SKIP_SUBMIT) return SKIP_SUBMIT;
      return onSubmit(payload as any);
    },
    onSuccess: (_engineMode, result) => {
      if (result === SKIP_SUBMIT) return;
      if (mode === "add") {
        setTimeout(() => {
          setResetCounter((c) => c + 1);
        }, 100);
      }
    },
  };
}

/* ==========================================================================
 * Contract
 * ======================================================================= */

export interface ContractFormProps {
  contract?: any;
  onSubmit: (data: Partial<ContractRequest>) => Promise<any>;
  onCancel: () => void;
  isSubmitting?: boolean;
  isMainApiCompleted?: boolean;
  resetTrigger?: number;
  prefilledData?: any;
  onRefreshList?: () => Promise<void>;
  tags?: {
    insert?: string[];
    delete?: number[];
  };
}

interface ContractFormValues {
  subject: string;
  description: string;
  due_datetime: string;
  expiration_datetime: string;
  product_n_services: string;
  amount: number | undefined;
  grand_total: number | undefined;
  currency_code: string;
  company_id: SelectOption | null;
  _companyContactKey: number | null;
  primary_contact_id: SelectOption | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  proposal_id: number | null;
  contract_type_id: number | null;
}

interface ContractOriginalSnapshot {
  subject: string;
  description: string;
  due_datetime: string;
  expiration_datetime: string;
  product_n_services: string;
  amount: number | undefined;
  grand_total: number | undefined;
  currency_code: string;
  company_id: number | null;
  primary_contact_id: number | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  proposal_id: number | null;
  contract_type_id: number | null;
}

export function useContractFormConfig({
  contract,
  onSubmit,
  onCancel,
  isSubmitting = false,
  resetTrigger = 0,
  prefilledData,
  onRefreshList,
}: ContractFormProps): UniversalFormConfig<ContractFormValues> {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const {
    teams,
    contractTypes,
    proposals,
    loading: dataLoading,
    error: dataError,
  } = useContractFormData();

  const contractResetKey = contract?.contract_id
    ? `contract-${contract.contract_id}`
    : prefilledData
    ? `prefilled-${JSON.stringify(prefilledData?.proposal_id ?? "")}`
    : `new-${resetTrigger}`;

  const [teamMembers, setTeamMembers] = useState<SelectOption[]>([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);

  const [selectedTeamIdForUi, setSelectedTeamIdForUi] = useState<number | null>(
    contract?.assigned_team?.team_id ?? null
  );

  const [hasUnsavedProductChanges, setHasUnsavedProductChanges] = useState(false);

  const [tenantInfo, setTenantInfo] = useState<
    | {
        is_service_tax?: boolean;
        is_gst_tax?: boolean;
        is_vat_tax?: boolean;
        service_tax_default_percentage?: number;
        gst_tax_default_percentage?: number;
        vat_tax_default_percentage?: number;
      }
    | undefined
  >(undefined);

  const [hasGateCompanies, setHasGateCompanies] = useState(false);
  const latestTeamRequestRef = useRef<number | null>(null);

  const fetchTeamMembers = useCallback(async (teamId: number) => {
    latestTeamRequestRef.current = teamId;
    if (!teamId) {
      setTeamMembers([]);
      return;
    }
    setIsLoadingTeamMembers(true);
    try {
      const members = await FormLookupsService.fetchTeamMembers(teamId);
      if (latestTeamRequestRef.current !== teamId) return;
      setTeamMembers(members);
    } finally {
      if (latestTeamRequestRef.current === teamId) setIsLoadingTeamMembers(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedTenantId) return;
    const fetchTenantInfo = async () => {
      try {
        const data = await cachedRequest(`tenant-info:${selectedTenantId}`, async () => {
          const authHeader = localStorage.getItem("authCred");
          if (!authHeader) return null;
          const response = await fetch(`${API_BASE_URL}/tenant/${selectedTenantId}/`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          });
          if (!response.ok) return null;
          return await response.json();
        });
        if (!data) return;
        setTenantInfo({
          is_service_tax: data.is_service_tax,
          is_gst_tax: data.is_gst_tax,
          is_vat_tax: data.is_vat_tax,
          service_tax_default_percentage: data.service_tax_default_percentage,
          gst_tax_default_percentage: data.gst_tax_default_percentage,
          vat_tax_default_percentage: data.vat_tax_default_percentage,
        });
      } catch (err) {
        console.error("Error fetching tenant info:", err);
      }
    };
    fetchTenantInfo();
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setHasGateCompanies(false);
    CompanyService.getCompaniesByPage(1, 50, "", true)
      .then((res) => {
        if (!cancelled) setHasGateCompanies(res.companies.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasGateCompanies(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  useEffect(() => {
    const teamId =
      contract?.assigned_team?.team_id || (!contract ? prefilledData?.assigned_team_id : null);
    if (teamId) {
      fetchTeamMembers(teamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractResetKey]);

  const existingProductsServices: any[] = useMemo(() => {
    if (contract) {
      return Array.isArray((contract as any).product_n_service)
        ? (contract as any).product_n_service
        : [];
    }
    if (
      prefilledData?.existingProductsServices &&
      Array.isArray(prefilledData.existingProductsServices)
    ) {
      return prefilledData.existingProductsServices;
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractResetKey]);

  const prefilledProductsServicesSeed: any[] = useMemo(() => {
    if (
      !contract &&
      prefilledData?.existingProductsServices &&
      Array.isArray(prefilledData.existingProductsServices)
    ) {
      return prefilledData.existingProductsServices.map((item: any) => ({
        product_n_service_id:
          item.product_n_service?.product_n_service_id || item.product_n_service_id,
        quantity: item.quantity || 1,
        discount_percentage: item.discount_percentage || 0,
        discounted_total: item.discounted_total || 0,
        gross_total: item.gross_total || 0,
        service_tax_percentage: item.service_tax_percentage || 0,
        gst_tax_percentage: item.gst_tax_percentage || 0,
        vat_tax_percentage: item.vat_tax_percentage || 0,
        service_tax_total: item.service_tax_total || 0,
        gst_tax_total: item.gst_tax_total || 0,
        vat_tax_total: item.vat_tax_total || 0,
        product_n_service: item.product_n_service || {
          product_n_service_id: item.product_n_service_id,
          name: item.name || "Product/Service",
          price: item.price || 0,
        },
      }));
    }
    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractResetKey]);

  const [prefilledAttachmentFiles, setPrefilledAttachmentFiles] = useState<File[]>([]);

  useEffect(() => {
    if (
      contract ||
      !prefilledData?.existingAttachments ||
      !Array.isArray(prefilledData.existingAttachments)
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      const filePromises = prefilledData.existingAttachments.map(async (attachment: any) => {
        try {
          const response = await fetch(attachment.attachment_url);
          const blob = await response.blob();
          const fileName = attachment.title || `attachment_${Date.now()}`;
          const file = new File([blob], fileName, {
            type: blob.type || "application/octet-stream",
          });

          Object.defineProperty(file, "fromProposal", {
            value: true,
            writable: false,
          });
          Object.defineProperty(file, "originalAttachment", {
            value: attachment,
            writable: false,
          });

          return file;
        } catch (err) {
          console.error("Error converting attachment to file:", err);
          return null;
        }
      });

      const convertedFiles = (await Promise.all(filePromises)).filter(
        (file: File | null): file is File => file !== null
      );
      if (!cancelled) setPrefilledAttachmentFiles(convertedFiles);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractResetKey]);

  const { initialValues, originalSnapshot } = useMemo(() => {
    if (contract) {
      const assignedUserId =
        contract.assigned_user && Object.keys(contract.assigned_user).length > 0
          ? contract.assigned_user.user_id || null
          : null;
      const assignedTeamId =
        contract.assigned_team && Object.keys(contract.assigned_team).length > 0
          ? contract.assigned_team.team_id || null
          : null;
      const companyId =
        contract.company && Object.keys(contract.company).length > 0
          ? contract.company.company_id || null
          : null;
      const proposalId =
        contract.proposal && Object.keys(contract.proposal).length > 0
          ? contract.proposal.proposal_id || null
          : null;
      const contractTypeId = contract.contract_type?.contract_type_id || null;
      const primaryContactId =
        contract.primary_contact && Object.keys(contract.primary_contact).length > 0
          ? contract.primary_contact.contact_id || null
          : null;

      const values: ContractFormValues = {
        subject: contract.subject || "",
        description: contract.description || "",
        due_datetime: contract.due_datetime || "",
        expiration_datetime: contract.expiration_datetime || "",
        product_n_services: contract.product_n_services || "",
        amount: contract.amount || undefined,
        grand_total: contract.grand_total || undefined,
        currency_code: contract.currency_code || "",
        company_id: companyId
          ? { value: companyId, label: contract.company?.company_name || "" }
          : null,
        _companyContactKey: companyId,
        primary_contact_id: primaryContactId
          ? {
              value: primaryContactId,
              label: `${contract.primary_contact?.first_name || ""} ${
                contract.primary_contact?.last_name || ""
              }`.trim(),
            }
          : null,
        assigned_team_id: assignedTeamId,
        assigned_user_id: assignedUserId,
        proposal_id: proposalId,
        contract_type_id: contractTypeId,
      };

      const snapshot: ContractOriginalSnapshot = {
        subject: values.subject,
        description: values.description,
        due_datetime: values.due_datetime,
        expiration_datetime: values.expiration_datetime,
        product_n_services: values.product_n_services,
        amount: values.amount,
        grand_total: values.grand_total,
        currency_code: values.currency_code,
        company_id: companyId,
        primary_contact_id: primaryContactId,
        assigned_team_id: assignedTeamId,
        assigned_user_id: assignedUserId,
        proposal_id: proposalId,
        contract_type_id: contractTypeId,
      };

      return { initialValues: values, originalSnapshot: snapshot };
    }

    if (prefilledData) {
      const contractTypeIdDefault =
        prefilledData.contract_type_id ||
        (contractTypes.length > 0 ? contractTypes[0].contract_type_id : null);

      const values: ContractFormValues = {
        subject: prefilledData.subject || "",
        description: prefilledData.description || "",
        due_datetime: prefilledData.due_datetime || "",
        expiration_datetime: prefilledData.expiration_datetime || "",
        product_n_services: prefilledData.product_n_services || "",
        amount: prefilledData.amount || undefined,
        grand_total: prefilledData.grand_total || undefined,
        currency_code: prefilledData.currency_code || "",
        company_id: prefilledData.company_id
          ? {
              value: prefilledData.company_id,
              label: prefilledData.company?.company_name || "",
            }
          : null,
        _companyContactKey: prefilledData.company_id || null,
        primary_contact_id: prefilledData.primary_contact_id
          ? { value: prefilledData.primary_contact_id, label: "" }
          : null,
        assigned_team_id: prefilledData.assigned_team_id || null,
        assigned_user_id: prefilledData.assigned_user_id || null,
        proposal_id: prefilledData.proposal_id || null,
        contract_type_id: contractTypeIdDefault,
      };

      return { initialValues: values, originalSnapshot: null as ContractOriginalSnapshot | null };
    }

    const values: ContractFormValues = {
      subject: "",
      description: "",
      due_datetime: "",
      expiration_datetime: "",
      product_n_services: "",
      amount: undefined,
      grand_total: undefined,
      currency_code: "",
      company_id: null,
      _companyContactKey: null,
      primary_contact_id: null,
      assigned_team_id: null,
      assigned_user_id: null,
      proposal_id: null,
      contract_type_id: null,
    };
    return { initialValues: values, originalSnapshot: null as ContractOriginalSnapshot | null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractResetKey, contractTypes]);

  const isDirty = useCallback(
    (ctx: FormCtx<ContractFormValues>): boolean => {
      if (!contract || !originalSnapshot) return true;

      const currentCompanyId = ctx.values.company_id?.value ?? null;
      const currentContactId = ctx.values.primary_contact_id?.value ?? null;
      const tagsDiff = ctx.getTagsDiff();

      return (
        ctx.values.subject !== originalSnapshot.subject ||
        ctx.values.description !== originalSnapshot.description ||
        ctx.values.due_datetime !== originalSnapshot.due_datetime ||
        ctx.values.expiration_datetime !== originalSnapshot.expiration_datetime ||
        ctx.values.product_n_services !== originalSnapshot.product_n_services ||
        ctx.values.amount !== originalSnapshot.amount ||
        ctx.values.currency_code !== originalSnapshot.currency_code ||
        ctx.values.assigned_user_id !== originalSnapshot.assigned_user_id ||
        ctx.values.assigned_team_id !== originalSnapshot.assigned_team_id ||
        currentCompanyId !== originalSnapshot.company_id ||
        currentContactId !== originalSnapshot.primary_contact_id ||
        ctx.values.proposal_id !== originalSnapshot.proposal_id ||
        ctx.values.contract_type_id !== originalSnapshot.contract_type_id ||
        ctx.attachments.length > 0 ||
        ctx.attachmentsToDelete.length > 0 ||
        hasUnsavedProductChanges ||
        tagsDiff.insert.length > 0 ||
        tagsDiff.delete.length > 0 ||
        ctx.hasCustomFieldChanges
      );
    },
    [contract, originalSnapshot, hasUnsavedProductChanges]
  );

  const buildSubmitPayload = useCallback(
    async (ctx: FormCtx<ContractFormValues>, mode: "create" | "edit") => {
      const validProductsServices = buildValidProductsServices(
        ctx.productsServices,
        selectedTenantId
      );

      if (mode === "edit") {
        if (ctx.attachmentsToDelete.length > 0) {
          const authHeader = localStorage.getItem("authCred");
          if (!authHeader) {
            throw new Error("Authentication required");
          }
          for (const attachmentId of ctx.attachmentsToDelete) {
            const response = await fetch(
              `${API_BASE_URL}/attachment/${attachmentId}/?tenant_id=${selectedTenantId}`,
              {
                method: "DELETE",
                headers: { Authorization: `Basic ${authHeader}` },
              }
            );
            if (!response.ok) {
              throw new Error(`Failed to delete attachment ${attachmentId}`);
            }
          }
        }

        const tagsDiff = ctx.getTagsDiff();
        const changes: any = {
          updated_by: auth?.user_id || undefined,
          tenant_id: Number(selectedTenantId),
        };

        if (tagsDiff.insert.length > 0 || tagsDiff.delete.length > 0) {
          changes.tags = { insert: tagsDiff.insert, delete: tagsDiff.delete };
        }

        if (originalSnapshot) {
          Object.assign(
            changes,
            buildChangedFields(ctx.values as any, originalSnapshot as any, [
              "subject",
              "currency_code",
            ])
          );

          if (ctx.values.description !== originalSnapshot.description) {
            changes.description = ctx.values.description || undefined;
          }
          if (ctx.values.due_datetime !== originalSnapshot.due_datetime) {
            changes.due_datetime = ctx.values.due_datetime || undefined;
          }
          if (ctx.values.expiration_datetime !== originalSnapshot.expiration_datetime) {
            changes.expiration_datetime = ctx.values.expiration_datetime || undefined;
          }
          if (ctx.values.product_n_services !== originalSnapshot.product_n_services) {
            changes.product_n_services = ctx.values.product_n_services || undefined;
          }
          if (ctx.values.amount !== originalSnapshot.amount) {
            changes.amount = ctx.values.amount || undefined;
          }
          if (ctx.values.grand_total !== originalSnapshot.grand_total) {
            changes.grand_total = ctx.values.grand_total || undefined;
          }

          const currentCompanyId = ctx.values.company_id?.value ?? null;
          if (currentCompanyId !== originalSnapshot.company_id) {
            changes.company_id = currentCompanyId || undefined;
          }

          const currentContactId = ctx.values.primary_contact_id?.value ?? null;
          if (currentContactId !== originalSnapshot.primary_contact_id) {
            changes.primary_contact = currentContactId || undefined;
          }

          if (ctx.values.assigned_user_id !== originalSnapshot.assigned_user_id) {
            changes.assigned_user_id = ctx.values.assigned_user_id || undefined;
          }
          if (ctx.values.assigned_team_id !== originalSnapshot.assigned_team_id) {
            changes.assigned_team_id = ctx.values.assigned_team_id || undefined;
          }
          if (ctx.values.proposal_id !== originalSnapshot.proposal_id) {
            changes.proposal_id = ctx.values.proposal_id ?? null;
          }
          if (ctx.values.contract_type_id !== originalSnapshot.contract_type_id) {
            changes.contract_type_id = ctx.values.contract_type_id || undefined;
          }
        }

        if (ctx.attachments.length > 0) {
          changes.attachment_url = ctx.attachments;
        }

        const willUseFormData = ctx.attachments.length > 0 || ctx.attachmentsToDelete.length > 0;

        if (ctx.tags.length > 0) {
          const tagsPayload = { insert: tagsDiff.insert, delete: tagsDiff.delete };
          changes.tags = willUseFormData ? JSON.stringify(tagsPayload) : tagsPayload;
        }

        if (validProductsServices.length > 0) {
          changes.product_n_service = willUseFormData
            ? JSON.stringify(validProductsServices)
            : validProductsServices;
        }

        const customFieldsArray = await ctx.getCustomFieldsPayload();
        if (customFieldsArray.length > 0) {
          changes.custom_fields = willUseFormData
            ? JSON.stringify(customFieldsArray)
            : customFieldsArray;
        }

        return changes;
      }

      const allAttachmentFiles = [...ctx.attachments, ...prefilledAttachmentFiles];
      const willUseFormData = allAttachmentFiles.length > 0;

      const payload: any = {
        subject: ctx.values.subject,
        description: ctx.values.description || undefined,
        due_datetime: ctx.values.due_datetime || undefined,
        expiration_datetime: ctx.values.expiration_datetime || undefined,
        product_n_services: ctx.values.product_n_services || undefined,
        amount: ctx.values.amount || undefined,
        grand_total: ctx.values.grand_total || undefined,
        currency_code: ctx.values.currency_code,
        company_id: ctx.values.company_id!.value,
        primary_contact: ctx.values.primary_contact_id?.value || undefined,
        assigned_user_id: ctx.values.assigned_user_id || undefined,
        assigned_team_id: ctx.values.assigned_team_id || undefined,
        proposal_id: ctx.values.proposal_id || undefined,
        contract_type_id: ctx.values.contract_type_id!,
        tenant_id: Number(selectedTenantId),
        created_by: auth!.user_id,
        updated_by: auth!.user_id,
        tags: willUseFormData
          ? JSON.stringify({ insert: ctx.tags.map((t) => t.entity_tag_name) })
          : { insert: ctx.tags.map((t) => t.entity_tag_name) },
      };

      if (allAttachmentFiles.length > 0) {
        payload.attachment_url = allAttachmentFiles;
      }

      if (validProductsServices.length > 0) {
        payload.product_n_service = willUseFormData
          ? JSON.stringify(validProductsServices)
          : validProductsServices;
      }

      const customFieldsArray = await ctx.getCustomFieldsPayload();
      if (customFieldsArray.length > 0) {
        payload.custom_fields = willUseFormData
          ? JSON.stringify(customFieldsArray)
          : customFieldsArray;
      }

      return payload;
    },
    [auth, selectedTenantId, originalSnapshot, prefilledAttachmentFiles]
  );

  const teamOptions: SelectOption[] = teams.map((team: any) => ({
    value: team.team_id,
    label: team.team_name || team.name,
  }));

  const contractTypeOptions: SelectOption[] = contractTypes.map((type: any) => ({
    value: type.contract_type_id,
    label: type.contract_type_display_name,
  }));

  const proposalOptionsFn = (ctx: FormCtx<ContractFormValues>): SelectOption[] => {
    let proposalsToShow = proposals.filter((p: any) => !p.is_archive);
    const currentId = ctx.values.proposal_id;

    if (contract && currentId) {
      const currentProposal = proposals.find((p: any) => p.proposal_id === currentId);
      if (currentProposal && !proposalsToShow.find((p: any) => p.proposal_id === currentId)) {
        proposalsToShow = [...proposalsToShow, currentProposal];
      }
    }

    const opts: SelectOption[] = proposalsToShow.map((p: any) => ({
      value: p.proposal_id,
      label: p.subject,
    }));

    if (currentId != null) {
      const label =
        proposals.find((p: any) => p.proposal_id === currentId)?.subject ||
        (contract?.proposal?.proposal_id === currentId ? contract?.proposal?.subject : undefined) ||
        `${getLifecycleTypeName("PROPOSAL")} ID: ${currentId}`;
      const idx = opts.findIndex((o) => o.value === currentId);
      if (idx >= 0) {
        opts[idx] = { value: currentId, label };
      } else {
        opts.push({ value: currentId, label });
      }
    }

    return opts;
  };

  const showLoadingGate =
    dataLoading && (!hasGateCompanies || !contractTypes.length);

  return {
    formId: "contract-form",
    mode: contract ? "edit" : "create",
    record: contract ?? null,
    resetKey: contractResetKey,
    tenantId: selectedTenantId,
    initialValues,
    loading: dataError ? true : showLoadingGate,
    loadingContent: dataError ? (
      <div>Error loading form data: {dataError}</div>
    ) : undefined,
    isSubmitting,
    submitLabel: contract
      ? `Update ${getLifecycleTypeName("CONTRACT")}`
      : `Create ${getLifecycleTypeName("CONTRACT")}`,
    submittingLabel: contract ? "Updating..." : "Creating...",
    onCancel,
    isDirty,
    sections: [
      {
        fields: [
          {
            name: "subject",
            label: "Subject",
            type: "text",
            colClass: "col-md-12",
            required: true,
            requiredMessage: `${getLifecycleTypeName("CONTRACT")} subject is required`,
            controlProps: {
              placeholder: `Enter ${getLifecycleTypeName("CONTRACT")} subject`,
            },
          },
          {
            name: "due_datetime",
            label: "Due Date & Time",
            type: "datetime",
            colClass: "col-md-6",
            controlProps: {
              placeholderText: "Select due date and time",
              dateFormat: "yyyy-MM-dd HH:mm",
              timeFormat: "HH:mm",
              timeCaption: "Time",
              isClearable: true,
              closeOnScroll: false,
              popperPlacement: "bottom-start",
            },
          },
          {
            name: "expiration_datetime",
            label: "Expiration Date & Time",
            type: "datetime",
            colClass: "col-md-6",
            controlProps: {
              placeholderText: "Select expiration date and time",
              dateFormat: "yyyy-MM-dd HH:mm",
              timeFormat: "HH:mm",
              timeCaption: "Time",
              isClearable: true,
              closeOnScroll: false,
              popperPlacement: "bottom-start",
            },
          },
          {
            name: "description",
            label: "Description",
            type: "textarea",
            colClass: "col-md-12",
            controlProps: { placeholder: "Enter description", rows: 4 },
          },
          {
            name: "company_id",
            label: "Company",
            type: "async-select",
            colClass: "col-md-12",
            required: true,
            requiredMessage: "Company is required",
            isClearable: true,
            placeholder: "Select company",
            loadOptions: async (page, search) => {
              const res = await CompanyService.getCompaniesByPage(page, 50, search, true);
              return {
                options: res.companies
                  .filter((c) => c.company_id !== undefined)
                  .map((c) => ({ value: c.company_id as number, label: c.company_name })),
                hasMore: res.hasMore,
              };
            },
            onChange: (value: SelectOption | null, ctx) => {
              ctx.setValue("_companyContactKey", value?.value ?? null);
              ctx.setValue("primary_contact_id", null);
            },
          },
          {
            name: "proposal_id",
            label: `Related ${getLifecycleTypeName("PROPOSAL")}`,
            type: "select",
            colClass: "col-md-12",
            isClearable: true,
            placeholder: `Select ${getLifecycleTypeName("PROPOSAL")}`,
            options: proposalOptionsFn,
            visible: () => !prefilledData?.proposal_id,
          },
          {
            name: "primary_contact_id",
            label: "Primary Contact",
            type: "async-select",
            colClass: "col-md-12",
            isClearable: true,
            dependsOn: "_companyContactKey",
            disabled: (ctx) => (ctx.values.primary_contact_id ? false : !ctx.values.company_id),
            placeholder: (ctx) =>
              ctx.values.company_id ? "Select contact" : "Select a company first",
            loadOptions: (page, _search, ctx) =>
              fetchContactsForCompany(selectedTenantId, ctx.values.company_id?.value, page),
          },
          {
            name: "assigned_team_id",
            label: "Assigned Team",
            required: true,
            type: "select",
            colClass: "col-md-12",
            isClearable: true,
            placeholder: "Select assigned team",
            options: teamOptions,
            onChange: (value: number | null, ctx) => {
              ctx.setValue("assigned_user_id", null);
              setSelectedTeamIdForUi(value ?? null);
              if (value) {
                fetchTeamMembers(value);
              } else {
                setTeamMembers([]);
              }
            },
          },
          {
            name: "assigned_user_id",
            label: "Assigned User",
            type: "select",
            colClass: "col-md-12",
            isClearable: true,
            options: teamMembers,
            disabled: (ctx) =>
              !ctx.values.assigned_team_id || isLoadingTeamMembers || teamMembers.length === 0,
            placeholder: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Please select a team first"
                : teamMembers.length === 0
                ? "No users in selected team"
                : "Select user from team",
            helperText: (ctx) =>
              !ctx.values.assigned_team_id ? "Select a team first to assign users" : "",
            controlProps: {
              isLoading: isLoadingTeamMembers,
              noOptionsMessage: () =>
                !selectedTeamIdForUi
                  ? "Please select a team first"
                  : "No users found in selected team",
            },
          },
          {
            name: "contract_type_id",
            label: `${getLifecycleTypeName("CONTRACT")} Type`,
            type: "select",
            colClass: "col-md-12",
            required: true,
            requiredMessage: `${getLifecycleTypeName("CONTRACT")} type is required`,
            isClearable: true,
            placeholder: `Select ${getLifecycleTypeName("CONTRACT")} type`,
            options: contractTypeOptions,
          },
          {
            name: "currency_code",
            label: "Currency",
            type: "currency",
            colClass: "col-md-6",
            placeholder: "Select currency",
            controlProps: { isClearable: false },
          },
          {
            name: "grand_total",
            type: "custom",
            render: (ctx) => (
              <div className="col-md-6 mb-3">
                <label className="form-label">Amount</label>
                <input
                  className="form-control"
                  type="number"
                  name="grand_total"
                  value={ctx.values.grand_total || ""}
                  placeholder="Auto-calculated from products/services"
                  min="0"
                  step="any"
                  readOnly
                  disabled
                />
              </div>
            ),
          },
          {
            name: "products_services_selector",
            type: "products-services",
            colClass: "mb-4",
            label: "Products / Services",
            productsServices: {
              entityType: "contract",
              entityId: contract?.contract_id,
              existingItems: existingProductsServices,
              seedItems: prefilledProductsServicesSeed,
              remountKey: `products-services-${existingProductsServices.length}-${
                contract?.contract_id || "new"
              }`,
              sumFields: [
                "discounted_total",
                "service_tax_total",
                "gst_tax_total",
                "vat_tax_total",
              ],
              writeSumTo: ["amount", "grand_total"],
              skipSumWhenEmpty: true,
              onUnsavedChanges: setHasUnsavedProductChanges,
              tenantInfo,
            },
          },
        ],
      },
    ],
    tags: {
      enabled: true,
      initial: (contract?.tags as any) ?? null,
      debounceMs: 1000,
    },
    attachments: {
      enabled: true,
      existingAttachments: contract?.attachments || [],
      maxFiles: 5,
      maxFileSizeMB: 10,
    },
    customFields: { entityKey: "contract" },
    buildSubmitPayload,
    onSubmit: (payload) => onSubmit(payload),
    onSuccess: (mode, result) => {
      if (mode === "edit") {
        MySwal.fire({
          icon: "success",
          title: "Updated!",
          text: `${getLifecycleTypeName("CONTRACT")} has been updated successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });
        if (onRefreshList) {
          setTimeout(() => onRefreshList(), 1000);
        }
      } else if (result && result.contract_id) {
        if (onRefreshList) {
          onRefreshList();
        }
      }
    },
  };
}

/* ==========================================================================
 * Proposal
 * ======================================================================= */

export interface ProposalFormProps {
  proposal?: any;
  onSubmit: (data: Partial<ProposalRequest>) => Promise<any>;
  onCancel: () => void;
  resetTrigger?: number;
  isMainApiCompleted?: boolean;
  isSubmitting?: boolean;
  prefilledData?: any;
  onRefreshList?: () => Promise<void>;
  formId?: string;
  tags?: {
    insert?: string[];
    delete?: number[];
  };
}

interface ProposalFormValues {
  subject: string;
  description: string;
  start_datetime: string;
  expiration_datetime: string;
  product_n_services: string;
  amount: number | undefined;
  grand_total: number | undefined;
  currency_code: string;
  company_id: SelectOption | null;
  primary_contact_id: SelectOption | null;
  deal_id: number | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  proposal_status_id: number | null;
}

interface ProposalOriginalSnapshot {
  subject: string;
  description: string;
  start_datetime: string;
  expiration_datetime: string;
  product_n_services: string;
  amount: number | undefined;
  grand_total: number | undefined;
  currency_code: string;
  company_id: number | null;
  primary_contact_id: number | null;
  deal_id: number | null;
  assigned_team_id: number | null;
  assigned_user_id: number | null;
  proposal_status_id: number | null;
}

export function useProposalFormConfig({
  proposal,
  onSubmit,
  onCancel,
  resetTrigger,
  isSubmitting = false,
  prefilledData,
  onRefreshList,
  formId = "default",
}: ProposalFormProps): UniversalFormConfig<ProposalFormValues> {
  const { auth, selectedTenantId } = useContext(AuthContext);
  const {
    teams,
    proposalStatuses,
    deals,
    loading: dataLoading,
    error: dataError,
  } = useProposalFormData();

  const [tenantInfo, setTenantInfo] = useState<
    | {
        is_service_tax?: boolean;
        is_gst_tax?: boolean;
        is_vat_tax?: boolean;
        service_tax_default_percentage?: number;
        gst_tax_default_percentage?: number;
        vat_tax_default_percentage?: number;
        proposal_min_discount_percentage?: number;
        proposal_max_discount_percentage?: number;
      }
    | undefined
  >(undefined);

  const [teamMembers, setTeamMembers] = useState<SelectOption[]>([]);
  const [isLoadingTeamMembers, setIsLoadingTeamMembers] = useState(false);

  const [selectedTeamIdForUi, setSelectedTeamIdForUi] = useState<
    number | null
  >(proposal?.assigned_team?.team_id ?? null);

  const [hasGateCompanies, setHasGateCompanies] = useState(false);

  const contactsCount = useContactsCountForCompany(selectedTenantId);
  const latestTeamRequestRef = useRef<number | null>(null);

  const fetchTeamMembers = useCallback(async (teamId: number) => {
    latestTeamRequestRef.current = teamId;
    if (!teamId) {
      setTeamMembers([]);
      return;
    }
    setIsLoadingTeamMembers(true);
    try {
      const members = await FormLookupsService.fetchTeamMembers(teamId);
      if (latestTeamRequestRef.current !== teamId) return;
      setTeamMembers(members);
    } finally {
      if (latestTeamRequestRef.current === teamId) setIsLoadingTeamMembers(false);
    }
  }, []);

  const fetchTenantInfo = useCallback(async () => {
    if (!selectedTenantId) return;
    try {
      const data = await cachedRequest(`tenant-info:${selectedTenantId}`, async () => {
        const authHeader = localStorage.getItem("authCred");
        if (!authHeader) return null;
        const response = await fetch(
          `${API_BASE_URL}/tenant/${selectedTenantId}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Basic ${authHeader}`,
            },
          }
        );
        if (!response.ok) return null;
        return await response.json();
      });
      if (!data) return;
      setTenantInfo({
        is_service_tax: data.is_service_tax,
        is_gst_tax: data.is_gst_tax,
        is_vat_tax: data.is_vat_tax,
        service_tax_default_percentage: data.service_tax_default_percentage,
        gst_tax_default_percentage: data.gst_tax_default_percentage,
        vat_tax_default_percentage: data.vat_tax_default_percentage,
        proposal_min_discount_percentage: data.proposal_min_discount_percentage,
        proposal_max_discount_percentage: data.proposal_max_discount_percentage,
      });
    } catch (error) {
      console.error("Error fetching tenant info:", error);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    if (selectedTenantId) {
      fetchTenantInfo();
    }
  }, [selectedTenantId, fetchTenantInfo]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setHasGateCompanies(false);
    CompanyService.getCompaniesByPage(1, 50, "", true)
      .then((res) => {
        if (!cancelled) setHasGateCompanies(res.companies.length > 0);
      })
      .catch(() => {
        if (!cancelled) setHasGateCompanies(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId]);

  useEffect(() => {
    const teamId = proposal
      ? proposal?.assigned_team?.team_id || null
      : prefilledData?.assigned_team_id || null;
    if (teamId) {
      fetchTeamMembers(teamId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal, prefilledData]);

  useEffect(() => {
    const companyId = proposal
      ? proposal?.company?.company_id || null
      : prefilledData?.company_id || null;
    contactsCount.checkFor(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal, prefilledData]);

  const existingProductsServices = useMemo(() => {
    if (proposal && Array.isArray((proposal as any).product_n_service)) {
      return (proposal as any).product_n_service;
    }
    return [];
  }, [proposal]);

  const { initialValues, originalSnapshot } = useMemo(() => {
    if (proposal) {
      const assignedUserId =
        proposal.assigned_user && Object.keys(proposal.assigned_user).length > 0
          ? proposal.assigned_user.user_id || null
          : null;
      const assignedTeamId =
        proposal.assigned_team && Object.keys(proposal.assigned_team).length > 0
          ? proposal.assigned_team.team_id || null
          : null;
      const companyId =
        proposal.company && Object.keys(proposal.company).length > 0
          ? proposal.company.company_id || null
          : null;
      const dealId =
        proposal.deal && Object.keys(proposal.deal).length > 0
          ? proposal.deal.deal_id || null
          : null;
      const proposalStatusId =
        proposal.proposal_status?.proposal_status_id || null;
      const primaryContactId =
        proposal.primary_contact &&
        Object.keys(proposal.primary_contact).length > 0
          ? proposal.primary_contact.contact_id || null
          : null;

      const values: ProposalFormValues = {
        subject: proposal.subject || "",
        description: proposal.description || "",
        start_datetime: proposal.start_datetime || "",
        expiration_datetime: proposal.expiration_datetime || "",
        product_n_services: proposal.product_n_services || "",
        amount: proposal.amount || undefined,
        grand_total: proposal.grand_total || undefined,
        currency_code: proposal.currency_code || "",
        company_id: companyId
          ? { value: companyId, label: proposal.company?.company_name || "" }
          : null,
        primary_contact_id: primaryContactId
          ? {
              value: primaryContactId,
              label: `${proposal.primary_contact?.first_name || ""} ${
                proposal.primary_contact?.last_name || ""
              }`.trim(),
            }
          : null,
        deal_id: dealId,
        assigned_team_id: assignedTeamId,
        assigned_user_id: assignedUserId,
        proposal_status_id: proposalStatusId,
      };

      const snapshot: ProposalOriginalSnapshot = {
        subject: values.subject,
        description: values.description,
        start_datetime: values.start_datetime,
        expiration_datetime: values.expiration_datetime,
        product_n_services: values.product_n_services,
        amount: values.amount,
        grand_total: values.grand_total,
        currency_code: values.currency_code,
        company_id: companyId,
        primary_contact_id: primaryContactId,
        deal_id: dealId,
        assigned_team_id: assignedTeamId,
        assigned_user_id: assignedUserId,
        proposal_status_id: proposalStatusId,
      };

      return { initialValues: values, originalSnapshot: snapshot };
    }

    if (prefilledData) {
      const defaultStatusId =
        proposalStatuses.length > 0
          ? proposalStatuses[0].proposal_status_id
          : null;

      let prefilledAmount = prefilledData.amount || undefined;
      let prefilledGrandTotal = prefilledData.grand_total || undefined;
      if (
        Array.isArray(prefilledData.prefilled_products_services) &&
        prefilledData.prefilled_products_services.length > 0
      ) {
        const totalFinalPrice = prefilledData.prefilled_products_services.reduce(
          (sum: number, item: any) =>
            sum +
            (item.discounted_total || 0) +
            (item.service_tax_total || 0) +
            (item.gst_tax_total || 0) +
            (item.vat_tax_total || 0),
          0
        );
        prefilledAmount = totalFinalPrice || undefined;
        prefilledGrandTotal = totalFinalPrice || undefined;
      }

      const values: ProposalFormValues = {
        subject: prefilledData.subject || "",
        description: prefilledData.description || "",
        start_datetime: prefilledData.start_datetime || "",
        expiration_datetime: prefilledData.expiration_datetime || "",
        product_n_services: prefilledData.product_n_services || "",
        amount: prefilledAmount,
        grand_total: prefilledGrandTotal,
        currency_code: prefilledData.currency_code || "",
        company_id: prefilledData.company_id
          ? {
              value: prefilledData.company_id,
              label: prefilledData.company?.company_name || "",
            }
          : null,
        primary_contact_id: prefilledData.primary_contact_id
          ? {
              value: prefilledData.primary_contact_id,
              label: prefilledData.contact?.full_name || "",
            }
          : null,
        deal_id: prefilledData.deal_id || null,
        assigned_team_id: prefilledData.assigned_team_id || null,
        assigned_user_id: prefilledData.assigned_user_id || null,
        proposal_status_id:
          prefilledData.proposal_status_id || defaultStatusId,
      };

      return { initialValues: values, originalSnapshot: null as ProposalOriginalSnapshot | null };
    }

    const values: ProposalFormValues = {
      subject: "",
      description: "",
      start_datetime: "",
      expiration_datetime: "",
      product_n_services: "",
      amount: undefined,
      grand_total: undefined,
      currency_code: "",
      company_id: null,
      primary_contact_id: null,
      deal_id: null,
      assigned_team_id: null,
      assigned_user_id: null,
      proposal_status_id: null,
    };

    return { initialValues: values, originalSnapshot: null as ProposalOriginalSnapshot | null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal, prefilledData, proposalStatuses]);

  const buildSubmitPayload = useCallback(
    async (ctx: FormCtx<ProposalFormValues>, mode: "create" | "edit") => {
      const validProductsServices = buildValidProductsServices(
        ctx.productsServices,
        selectedTenantId
      );
      const tagsDiff = ctx.getTagsDiff();
      const customFieldsArray = await ctx.getCustomFieldsPayload();
      const currentUserId = FormLookupsService.getUserIdWithProfileFallback(auth);

      if (mode === "edit") {
        if (ctx.attachmentsToDelete.length > 0) {
          const authHeader = localStorage.getItem("authCred");
          if (!authHeader) {
            throw new Error("Authentication required");
          }

          for (const attachmentId of ctx.attachmentsToDelete) {
            const response = await fetch(
              `${API_BASE_URL}/attachment/${attachmentId}/?tenant_id=${selectedTenantId}`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Basic ${authHeader}`,
                },
              }
            );

            if (!response.ok) {
              throw new Error(`Failed to delete attachment ${attachmentId}`);
            }
          }
        }

        const changes: any = {
          updated_by: currentUserId || undefined,
          tenant_id: Number(selectedTenantId),
        };

        if (originalSnapshot) {
          if (ctx.values.subject !== originalSnapshot.subject) {
            changes.subject = ctx.values.subject;
          }
          if (ctx.values.description !== originalSnapshot.description) {
            changes.description = ctx.values.description || undefined;
          }
          if (ctx.values.start_datetime !== originalSnapshot.start_datetime) {
            changes.start_datetime = ctx.values.start_datetime || undefined;
          }
          if (
            ctx.values.expiration_datetime !==
            originalSnapshot.expiration_datetime
          ) {
            changes.expiration_datetime =
              ctx.values.expiration_datetime || undefined;
          }
          if (
            ctx.values.product_n_services !==
            originalSnapshot.product_n_services
          ) {
            changes.product_n_services =
              ctx.values.product_n_services || undefined;
          }
          if (ctx.values.amount !== originalSnapshot.amount) {
            changes.amount = ctx.values.amount || undefined;
          }
          if (ctx.values.grand_total !== originalSnapshot.grand_total) {
            changes.grand_total = ctx.values.grand_total || undefined;
          }
          if (ctx.values.currency_code !== originalSnapshot.currency_code) {
            changes.currency_code = ctx.values.currency_code;
          }

          const currentCompanyId = ctx.values.company_id?.value ?? null;
          if (currentCompanyId !== originalSnapshot.company_id) {
            changes.company_id = currentCompanyId || undefined;
          }
          const currentPrimaryContactId =
            ctx.values.primary_contact_id?.value ?? null;
          if (currentPrimaryContactId !== originalSnapshot.primary_contact_id) {
            changes.primary_contact = currentPrimaryContactId || undefined;
          }
          if (ctx.values.assigned_user_id !== originalSnapshot.assigned_user_id) {
            changes.assigned_user_id = ctx.values.assigned_user_id || undefined;
          }
          if (ctx.values.assigned_team_id !== originalSnapshot.assigned_team_id) {
            changes.assigned_team_id = ctx.values.assigned_team_id || undefined;
          }
          if (ctx.values.deal_id !== originalSnapshot.deal_id) {
            changes.deal_id = ctx.values.deal_id ?? null;
          }
          if (
            ctx.values.proposal_status_id !==
            originalSnapshot.proposal_status_id
          ) {
            changes.proposal_status_id =
              ctx.values.proposal_status_id || undefined;
          }
        }

        if (tagsDiff.insert.length > 0 || tagsDiff.delete.length > 0) {
          changes.tags = { insert: tagsDiff.insert, delete: tagsDiff.delete };
        }

        if (ctx.attachments.length > 0) {
          changes.attachment_url = ctx.attachments;
        }

        const willUseFormData =
          ctx.attachments.length > 0 || ctx.attachmentsToDelete.length > 0;

        if (ctx.tags.length > 0) {
          const tagsPayload = { insert: tagsDiff.insert, delete: tagsDiff.delete };
          changes.tags = willUseFormData
            ? JSON.stringify(tagsPayload)
            : tagsPayload;
        }

        if (validProductsServices.length > 0) {
          changes.product_n_service = willUseFormData
            ? JSON.stringify(validProductsServices)
            : validProductsServices;
        }

        if (customFieldsArray.length > 0) {
          changes.custom_fields = willUseFormData
            ? JSON.stringify(customFieldsArray)
            : customFieldsArray;
        }

        return changes;
      }

      const payload: any = {
        subject: ctx.values.subject,
        description: ctx.values.description || undefined,
        start_datetime: ctx.values.start_datetime || undefined,
        expiration_datetime: ctx.values.expiration_datetime || undefined,
        product_n_services: ctx.values.product_n_services || undefined,
        amount: ctx.values.amount || undefined,
        grand_total: ctx.values.grand_total || undefined,
        currency_code: ctx.values.currency_code,
        company_id: ctx.values.company_id!.value as number,
        primary_contact: ctx.values.primary_contact_id?.value || undefined,
        assigned_user_id: ctx.values.assigned_user_id || undefined,
        assigned_team_id: ctx.values.assigned_team_id || undefined,
        deal_id: ctx.values.deal_id || undefined,
        proposal_status_id: ctx.values.proposal_status_id as number,
        tenant_id: Number(selectedTenantId),
        created_by: currentUserId || undefined,
        updated_by: currentUserId || undefined,
        tags:
          ctx.attachments.length > 0
            ? JSON.stringify({ insert: tagsDiff.insert })
            : { insert: tagsDiff.insert },
      };

      if (ctx.attachments.length > 0) {
        payload.attachment_url = ctx.attachments;
      }

      if (validProductsServices.length > 0) {
        payload.product_n_service =
          ctx.attachments.length > 0
            ? JSON.stringify(validProductsServices)
            : validProductsServices;
      }

      if (customFieldsArray.length > 0) {
        payload.custom_fields =
          ctx.attachments.length > 0
            ? JSON.stringify(customFieldsArray)
            : customFieldsArray;
      }

      return payload;
    },
    [auth, originalSnapshot, selectedTenantId]
  );

  const teamOptions: SelectOption[] = teams.map((team: any) => ({
    value: team.team_id,
    label: team.team_name || team.name,
  }));

  const proposalStatusOptions: SelectOption[] = proposalStatuses.map(
    (status: any) => ({
      value: status.proposal_status_id,
      label: status.proposal_status_display_name,
    })
  );

  const dealOptionsFn = (ctx: FormCtx<ProposalFormValues>): SelectOption[] => {
    const activeDeals = deals.filter((d: any) => !d.is_archive);
    const opts: SelectOption[] = activeDeals.map((d: any) => ({
      value: d.deal_id,
      label: d.title,
    }));

    const currentId = ctx.values.deal_id;
    if (proposal && currentId) {
      const label =
        deals.find((d: any) => d.deal_id === currentId)?.title ||
        (proposal?.deal?.deal_id === currentId ? proposal?.deal?.title : undefined) ||
        `${getLifecycleTypeName("DEAL")} ID: ${currentId}`;
      const idx = opts.findIndex((o) => o.value === currentId);
      if (idx >= 0) {
        opts[idx] = { value: currentId, label };
      } else {
        opts.push({ value: currentId, label });
      }
    }
    return opts;
  };

  const showLoadingGate =
    dataLoading &&
    (!hasGateCompanies || !proposalStatuses.length);

  const resetKey = proposal
    ? `proposal-${proposal.proposal_id}`
    : prefilledData
    ? `prefilled-${JSON.stringify(prefilledData?.deal_id ?? "")}`
    : `new-proposal-${resetTrigger ?? 0}`;

  return {
    formId,
    mode: proposal ? "edit" : "create",
    record: proposal ?? null,
    resetKey,
    tenantId: selectedTenantId,
    initialValues,
    loading: dataError ? true : showLoadingGate,
    loadingContent: dataError ? (
      <div>Error loading form data: {dataError}</div>
    ) : undefined,
    isSubmitting,
    submitLabel: proposal
      ? `Update ${getLifecycleTypeName("PROPOSAL")}`
      : `Create ${getLifecycleTypeName("PROPOSAL")}`,
    submittingLabel: proposal ? "Updating..." : "Creating...",
    onCancel,
    sections: [
      {
        fields: [
          {
            name: "subject",
            label: "Subject",
            type: "text",
            colClass: "col-md-12",
            required: true,
            requiredMessage: `${getLifecycleTypeName("PROPOSAL")} subject is required`,
            controlProps: {
              placeholder: `Enter ${getLifecycleTypeName("PROPOSAL")} subject`,
            },
          },
          {
            name: "start_datetime",
            label: "Start Date & Time",
            type: "datetime",
            colClass: "col-md-6",
            controlProps: {
              placeholderText: "Select due date and time",
              dateFormat: "yyyy-MM-dd HH:mm",
              timeFormat: "HH:mm",
              timeCaption: "Time",
              isClearable: true,
              closeOnScroll: false,
              popperPlacement: "bottom-start",
            },
            onChange: (value, ctx) => {
              if (value === null) ctx.setValue("start_datetime", "");
            },
          },
          {
            name: "expiration_datetime",
            label: "Expiration Date & Time",
            type: "datetime",
            colClass: "col-md-6",
            controlProps: {
              placeholderText: "Select expiration date and time",
              dateFormat: "yyyy-MM-dd HH:mm",
              timeFormat: "HH:mm",
              timeCaption: "Time",
              isClearable: true,
              closeOnScroll: false,
              popperPlacement: "bottom-start",
            },
            onChange: (value, ctx) => {
              if (value === null) ctx.setValue("expiration_datetime", "");
            },
          },
          {
            name: "description",
            label: "Description",
            type: "textarea",
            colClass: "col-md-12",
            controlProps: { placeholder: "Enter description", rows: 4 },
          },
          {
            name: "company_id",
            label: "Company",
            type: "async-select",
            colClass: "col-md-12",
            required: true,
            requiredMessage: "Company is required",
            isClearable: true,
            controlProps: { placeholder: "Select company" },
            loadOptions: async (page, search) => {
              const res = await CompanyService.getCompaniesByPage(
                page,
                50,
                search,
                true
              );
              return {
                options: res.companies
                  .filter((c) => c.company_id !== undefined)
                  .map((c) => ({
                    value: c.company_id as number,
                    label: c.company_name,
                  })),
                hasMore: res.hasMore,
              };
            },
            onChange: (value: SelectOption | null, ctx) => {
              ctx.setValue("primary_contact_id", null);
              contactsCount.checkFor(value?.value ?? null);
            },
          },
          {
            name: "primary_contact_id",
            label: "Primary Contact",
            type: "async-select",
            colClass: "col-md-12",
            isClearable: true,
            dependsOn: "company_id",
            disabled: (ctx) => (ctx.values.primary_contact_id ? false : (!ctx.values.company_id || contactsCount.count === 0)),
            placeholder: (ctx) =>
              ctx.values.company_id ? "Select contact" : "Select a company first",
            helperText: (ctx) =>
              ctx.values.company_id && contactsCount.count === 0 && !contactsCount.isLoading
                ? "No contacts available for this company"
                : "",
            controlProps: {
              noOptionsMessage: () => "No contacts found",
            },
            loadOptions: (page, _search, ctx) =>
              fetchContactsForCompany(
                selectedTenantId,
                ctx.values.company_id?.value ?? null,
                page
              ),
          },
          {
            name: "deal_id",
            label: `Related ${getLifecycleTypeName("DEAL")}`,
            type: "select",
            colClass: "col-md-12",
            isClearable: true,
            options: dealOptionsFn,
            controlProps: {
              placeholder: `Select ${getLifecycleTypeName("DEAL")}`,
            },
          },
          {
            name: "assigned_team_id",
            label: "Assigned Team",
            required: true,
            type: "select",
            colClass: "col-md-12",
            isClearable: true,
            options: teamOptions,
            controlProps: { placeholder: "Select assigned team" },
            onChange: (value: number | null, ctx) => {
              ctx.setValue("assigned_user_id", null);
              setSelectedTeamIdForUi(value ?? null);
              if (value) {
                fetchTeamMembers(value);
              } else {
                setTeamMembers([]);
              }
            },
          },
          {
            name: "assigned_user_id",
            label: "Assigned User",
            type: "select",
            colClass: "col-md-12",
            isClearable: true,
            options: teamMembers,
            disabled: (ctx) =>
              !ctx.values.assigned_team_id ||
              isLoadingTeamMembers ||
              teamMembers.length === 0,
            placeholder: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Please select a team first"
                : teamMembers.length === 0
                ? "No users in selected team"
                : "Select user from team",
            helperText: (ctx) =>
              !ctx.values.assigned_team_id
                ? "Select a team first to assign users"
                : "",
            controlProps: {
              isLoading: isLoadingTeamMembers,
              noOptionsMessage: () =>
                !selectedTeamIdForUi
                  ? "Please select a team first"
                  : "No users found in selected team",
            },
          },
          {
            name: "proposal_status_id",
            label: `${getLifecycleTypeName("PROPOSAL")} Status`,
            type: "select",
            colClass: "col-md-12",
            required: true,
            requiredMessage: `${getLifecycleTypeName("PROPOSAL")} status is required`,
            isClearable: true,
            options: proposalStatusOptions,
            controlProps: {
              placeholder: `Select ${getLifecycleTypeName("PROPOSAL")} status`,
            },
          },
          {
            name: "currency_code",
            label: "Currency",
            type: "currency",
            colClass: "col-md-6",
            controlProps: { isClearable: false, placeholder: "Select currency" },
            onChange: (value, ctx) => {
              if (!value) ctx.setValue("currency_code", readStoredCurrencyCode() || "USD");
            },
          },
          {
            name: "grand_total",
            type: "custom",
            render: (ctx) => (
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="col-form-label">Amount</label>
                  <input
                    className="form-control"
                    type="number"
                    name="grand_total"
                    value={ctx.values.grand_total ?? ""}
                    placeholder="Auto-calculated from products/services"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    readOnly
                    disabled
                  />
                </div>
              </div>
            ),
          },
          {
            name: "products_services_selector",
            type: "products-services",
            colClass: "col-md-12 mb-4",
            label: "Products / Services",
            productsServices: {
              entityType: "proposal",
              entityId: proposal?.proposal_id,
              existingItems: existingProductsServices,
              seedItems:
                !proposal && prefilledData?.prefilled_products_services
                  ? prefilledData.prefilled_products_services
                  : [],
              sumFields: [
                "discounted_total",
                "service_tax_total",
                "gst_tax_total",
                "vat_tax_total",
              ],
              writeSumTo: ["amount", "grand_total"],
              gateOnDiscountErrors: true,
              tenantInfo,
            },
          },
        ],
      },
    ],
    tags: {
      enabled: true,
      initial: (proposal?.tags as any) ?? null,
      debounceMs: 1000,
    },
    attachments: {
      enabled: true,
      maxFiles: 5,
      maxFileSizeMB: 10,
      existingAttachments: proposal?.attachments,
    },
    customFields: { entityKey: "lifecycle" },
    buildSubmitPayload,
    onSubmit: async (payload) => {
      const result = await onSubmit(payload);
      if (!proposal && result && onRefreshList) {
        await onRefreshList();
      }
      return result;
    },
    onSuccess: (mode) => {
      if (mode === "edit") {
        MySwal.fire({
          icon: "success",
          title: "Updated!",
          text: `${getLifecycleTypeName("PROPOSAL")} has been updated successfully.`,
          timer: 2000,
          showConfirmButton: false,
        });

        if (onRefreshList) {
          setTimeout(() => onRefreshList(), 1000);
        }
      }
    },
  };
}
